/**
 * Milestone Payment Dashboard — Full Interactive Implementation
 * Fetches from /api/accounting/payment-schedules (dedicated PaymentScheduleModel)
 * Actions: Create Schedule, Activate Schedule, Activate Milestone, Generate Invoice,
 *          Interactive Editable Invoice Drawer (Live Edit + Save Changes to DB), Download Invoice
 * Note: When a milestone is 'paid', editing & generation are removed, keeping only read-only View & Download.
 */
import { lineGross, printedLineNote } from '@jira-clone/shared-types';
import { useCompanyProfile } from '@/features/recruitment/api';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '@/shared/api/axios';
import { downloadHtmlAsPdf, pdfFileName } from '@/lib/downloadPdf';
import { buildReceiptHtml } from '@/utils/sales/receiptHtml';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { formatINR } from '@/shared/utils/currency';
import { AccountingSubNav } from '@/components/sales/AccountingSubNav';
import { generateFullTaxInvoiceHtml } from '@/utils/sales/quotationTemplateTokens';
import { useDocumentMasterData } from '@/shared/hooks/useDocumentMasterData';
import { useDocumentNumbering } from '@/shared/hooks/useDocumentNumbering';
import { useUser } from '@/shared/hooks/useAuth';
import { applyNumberFormat, normalizeFiscalYear } from '@/utils/sales/documentNumberFormat';
import { applyRunningBill, type RunningBillPreview } from '@/utils/sales/runningBillInvoice';
import { applyFinalInvoice, taxInvoiceCopyLabel } from '@/utils/sales/finalInvoiceDocument';
import {
  Loader2, BarChart3, Receipt, Clock, CheckCircle2, AlertTriangle,
  ChevronRight, FileText, RefreshCw, Plus, Play, Zap, Download,
  XCircle, CheckCheck, ChevronDown, ChevronUp, X, Save, Lock, Upload, BookOpen,
  ArrowLeft, Search, Filter, RotateCcw, Eye, Bell, Wallet,
} from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { cn, cardClassName } from '@jira-clone/ui';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
function phaseLabel(idx: number): string { return `(${ROMAN[idx] ?? idx + 1})`; }
function milestoneInvoiceLabel(invoiceNumber: string, idx: number): string {
  if (!invoiceNumber) return `Phase ${phaseLabel(idx)}`;
  if (invoiceNumber.includes('(')) return invoiceNumber;
  return `${invoiceNumber}${phaseLabel(idx)}`;
}

/** A schedule with more than one stage is billed stage by stage, as running bills. */
const isRunningBillSchedule = (sch: { paymentType?: string; milestones?: unknown[] }) =>
  sch.paymentType !== 'full_payment' && (sch.milestones?.length ?? 0) > 1;

/** One payment received against a milestone (advance against the proforma, or against the tax invoice). */
interface PaymentEntry {
  _id: string;
  amount: number;
  paymentMode?: string;
  referenceNo?: string;
  note?: string;
  paidAt?: string;
  proofId?: string | null;
  proofFileName?: string;
  recordedByName?: string;
}

interface Milestone {
  _id: string;
  payments?: PaymentEntry[];
  milestoneNumber: number;
  name: string;
  percentage: number;
  fixedAmount: number;
  amountDue: number;
  amountInvoiced: number;
  amountReceived: number;
  amountOutstanding: number;
  status: 'pending' | 'due' | 'proforma_pending_approval' | 'proforma_approved' | 'proforma_rejected' | 'invoice_pending_approval' | 'invoice_generated' | 'approved' | 'revision_needed' | 'invoice_rejected' | 'partially_paid' | 'paid' | 'overdue' | 'cancelled';
  invoiceNumber: string;
  invoiceRequestId: string | null;
  proformaNumber?: string;
  proformaRequestId?: string | null;
  rejectionReason?: string;
  revisionNote?: string;
  dueDate: string | null;
  triggerType: string;
  description?: string;
  receiptNo?: string;
  receiptGenerated?: boolean;
  receiptGeneratedAt?: string | null;
  // ── GST Allocation Fields (proportionally split from quotation-level GST) ──
  gstRate?: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  intraState?: boolean;
  milestoneTaxableAmountMinor?: number;
  milestoneCGSTMinor?: number;
  milestoneSGSTMinor?: number;
  milestoneIGSTMinor?: number;
  milestoneGSTMinor?: number;
  milestoneTotalMinor?: number;
  milestoneTaxableAmount?: number;
  milestoneCGST?: number;
  milestoneSGST?: number;
  milestoneIGST?: number;
  milestoneGST?: number;
  milestoneTotal?: number;
  glPosted?: boolean;
  // Legacy aliases
  netAmount?: number;
  taxAmount?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
}

interface Schedule {
  _id: string;
  quotationId: string | null;
  quotationNumber: string;
  clientName: string;
  clientCode?: string;
  clientId?: string | null;
  totalAmount: number;
  totalInvoiced: number;
  totalReceived: number;
  totalOutstanding: number;
  remainingAmount: number;
  paymentProgress: number;
  paymentType?: string;
  status: 'draft' | 'active' | 'partially_complete' | 'completed' | 'cancelled';
  milestones: Milestone[];
  createdAt: string;
  // Full-payment (no explicit milestones) schedules carry these directly.
  invoiceNumber?: string;
  baseInvoiceNumber?: string;
  proformaNumber?: string;
  proformaRequestId?: string | null;
  dueDate?: string | null;
  // Final tax invoice, raised once every stage's running bill is paid.
  finalInvoiceRequestId?: string | null;
  finalInvoiceNumber?: string;
  finalInvoiceStatus?: string;
  // How many copies of the final tax invoice have already been handed out — decides whether the next
  // one is the original or a Copy, and is the record of every issue.
  finalInvoiceCopies?: { downloadCount: number; lastDownloadedAt?: string | null; lastDownloadedBy?: string };
}

/** Every stage has an approved running bill and has been paid in full. */
const allStagesPaid = (sch: { milestones?: Array<{ status: string; invoiceNumber?: string }> }) =>
  (sch.milestones?.length ?? 0) > 1 && sch.milestones!.every((m) => m.status === 'paid' && Boolean(m.invoiceNumber));

/** The salesperson behind the quotation: the schedule's "assignee" for filtering. */
const schedulePreparedBy = (sch: Schedule): string => {
  const quot: any = sch.quotationId;
  const fromQuotation = quot && typeof quot === 'object' ? (quot.preparedBy || quot.requestedByName || '') : '';
  const created: any = (sch as any).createdBy;
  const fromCreator = created && typeof created === 'object' ? (created.displayName || created.name || '') : '';
  return String((sch as any).preparedBy || fromQuotation || (sch as any).createdByName || fromCreator || '').trim();
};
const FINAL_RAISED = ['invoice_pending_approval', 'sent', 'approved'];

interface QuotationOption {
  id: string;
  number: string;
  clientName: string;
  grandTotal: number;
  milestones: Array<{ name: string; percent: number; due: string }>;
}

/** A payment can be recorded while something is outstanding and the proforma has not been rejected or left unapproved. */
const NO_PAYMENT_STATUSES = ['proforma_pending_approval', 'proforma_rejected', 'revision_needed', 'invoice_rejected', 'cancelled', 'paid'];
const canRecordPayment = (ms: { status: string; amountDue?: number; amountReceived?: number; amountOutstanding?: number }) => {
  const outstanding = ms.amountOutstanding ?? Math.max(0, (ms.amountDue || 0) - (ms.amountReceived || 0));
  return outstanding > 0 && !NO_PAYMENT_STATUSES.includes(ms.status);
};

/**
 * A running bill / tax invoice can be requested, and the phase invoice downloaded, only once the
 * milestone has been paid in full (payments recorded and nothing outstanding).
 */
const isFullyReceived = (ms: { status?: string; amountDue?: number; amountReceived?: number; amountOutstanding?: number }) => {
  const received = ms.amountReceived || 0;
  const outstanding = ms.amountOutstanding ?? Math.max(0, (ms.amountDue || 0) - received);
  return received > 0 && outstanding <= 0.005;
};

/** The latest payment date on a milestone as an ISO string, or '' when none. */
const lastPaymentIso = (ms: { payments?: Array<{ paidAt?: string }> }): string => {
  let latest = 0;
  for (const p of ms.payments || []) {
    const t = p.paidAt ? new Date(p.paidAt).getTime() : NaN;
    if (Number.isFinite(t) && t > latest) latest = t;
  }
  return latest > 0 ? new Date(latest).toISOString() : '';
};

/** The date of the latest payment recorded on a milestone, as shown in the table. */
const lastPaidAt = (ms: { payments?: Array<{ paidAt?: string }> }): string => {
  const iso = lastPaymentIso(ms);
  return iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  bank_transfer: 'Bank transfer', upi: 'UPI', cheque: 'Cheque', cash: 'Cash', card: 'Card', other: 'Other',
};

const STATUS_CFG: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  pending: { label: 'Pending', dot: 'bg-slate-400', bg: 'bg-slate-100', text: 'text-slate-600' },
  due: { label: 'Due', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  proforma_pending_approval: { label: 'Awaiting Approval', dot: 'bg-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-950/60 border border-indigo-300 dark:border-indigo-800', text: 'text-indigo-800 dark:text-indigo-300' },
  proforma_approved: { label: 'Proforma Approved', dot: 'bg-indigo-500', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  proforma_rejected: { label: 'Proforma Rejected', dot: 'bg-rose-500', bg: 'bg-rose-100', text: 'text-rose-700' },
  invoice_pending_approval: { label: 'Pending Approval', dot: 'bg-amber-500 font-bold animate-pulse', bg: 'bg-amber-100 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-300' },
  invoice_generated: { label: 'Invoiced', dot: 'bg-blue-500', bg: 'bg-blue-100', text: 'text-blue-700' },
  approved: { label: 'Approved', dot: 'bg-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-300' },
  revision_needed: { label: 'Revision Needed', dot: 'bg-orange-500 animate-pulse', bg: 'bg-orange-100 dark:bg-orange-950/60 border border-orange-300 dark:border-orange-800', text: 'text-orange-800 dark:text-orange-300' },
  invoice_rejected: { label: 'Invoice Rejected', dot: 'bg-rose-500', bg: 'bg-rose-100 dark:bg-rose-950/60 border border-rose-300 dark:border-rose-800', text: 'text-rose-800 dark:text-rose-300' },
  partially_paid: { label: 'Partially Paid', dot: 'bg-orange-500', bg: 'bg-orange-100', text: 'text-orange-700' },
  paid: { label: 'Paid', dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  overdue: { label: 'Overdue', dot: 'bg-red-500', bg: 'bg-red-100', text: 'text-red-700' },
  cancelled: { label: 'Cancelled', dot: 'bg-slate-300', bg: 'bg-slate-50', text: 'text-slate-500' },
  draft: { label: 'Draft', dot: 'bg-slate-300', bg: 'bg-slate-100', text: 'text-slate-500' },
  active: { label: 'Active', dot: 'bg-indigo-500', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  partially_complete: { label: 'In Progress', dot: 'bg-blue-400', bg: 'bg-blue-50', text: 'text-blue-600' },
  completed: { label: 'Completed', dot: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-700' },
};

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

const STEPS = ['Pending', 'Due', 'Invoiced', 'Partially Paid', 'Paid'] as const;
const STEP_STATUS: Record<string, number> = {
  pending: 0,
  due: 1,
  proforma_pending_approval: 1,
  proforma_approved: 1,
  proforma_rejected: 1,
  invoice_pending_approval: 2,
  invoice_generated: 2,
  approved: 2,
  partially_paid: 3,
  paid: 4,
};

function MilestoneProgress({ status }: { status: string }) {
  const current = STEP_STATUS[status] ?? 0;
  return (
    <div className="flex items-center gap-0.5 mt-1">
      {STEPS.map((step, i) => (
        <div key={step} className="flex items-center">
          <div
            title={step}
            className={`w-2 h-2 rounded-full transition-all ${i < current ? 'bg-emerald-500' :
                i === current ? 'bg-indigo-500 ring-2 ring-indigo-200' :
                  'bg-slate-200 dark:bg-slate-700'
              }`}
          />
          {i < STEPS.length - 1 && (
            <div className={`w-4 h-0.5 ${i < current ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function CountdownBadge({ dueDate, status }: { dueDate: string | null; status: string }) {
  if (!dueDate || ['paid', 'cancelled'].includes(status)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 border border-rose-200 dark:border-rose-800 animate-pulse mt-1">
        <AlertTriangle className="w-3 h-3" /> OVERDUE ({Math.abs(diffDays)}d ago)
      </span>
    );
  }
  if (diffDays === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 mt-1">
        <Clock className="w-3 h-3" /> DUE TODAY
      </span>
    );
  }
  if (diffDays <= 7) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 mt-1">
        <Clock className="w-3 h-3" /> Due in {diffDays} day{diffDays > 1 ? 's' : ''} ({due.toLocaleDateString('en-IN')})
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 mt-1">
      Due: {due.toLocaleDateString('en-IN')} ({diffDays} days)
    </span>
  );
}

// ── Create Schedule Modal ─────────────────────────────────────────────────────

const DEFAULT_MILESTONES = [
  { name: 'Advance on order confirmation', percent: 30, due: 'On signing' },
  { name: 'On design sign-off', percent: 20, due: 'T + 15 days' },
  { name: 'On UAT completion', percent: 30, due: 'T + 45 days' },
  { name: 'On go-live / handover', percent: 20, due: 'T + 60 days' },
];

interface ScheduleSourceOption {
  id: string;
  type: 'proforma' | 'quotation';
  number: string;
  clientName: string;
  grandTotal: number;
  milestones: Array<{ name: string; percent: number; due: string }>;
  sourceQuotationId?: string;
  sourceQuotationNumber?: string;
  proformaNumber?: string;
  proformaRequestId?: string;
}

function CreateScheduleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [sourceType, setSourceType] = useState<'proforma' | 'quotation'>('proforma');
  const [proformas, setProformas] = useState<ScheduleSourceOption[]>([]);
  const [quotations, setQuotations] = useState<ScheduleSourceOption[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selected, setSelected] = useState<ScheduleSourceOption | null>(null);
  const [milestones, setMilestones] = useState<Array<{ name: string; percent: number; due: string; dueDate: string }>>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [clientName, setClientName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoadingDocs(true);
        const [resQ, resP] = await Promise.allSettled([
          api.get('/api/invoice-requests?requestType=quotation&limit=100'),
          api.get('/api/invoice-requests?requestType=proforma&limit=100'),
        ]);

        if (resQ.status === 'fulfilled') {
          const items = resQ.value.data?.requests || resQ.value.data?.data || [];
          setQuotations(items
            .filter((r: any) => r.status !== 'cancelled')
            .map((r: any) => ({
              id: r.id || r._id,
              type: 'quotation' as const,
              number: r.number || r.sourceQuotationNumber || '',
              clientName: r.clientName || '',
              grandTotal: r.total || r.grandTotal || 0,
              milestones: (r.template_data?.milestones || r.milestones || []).map((m: any) => ({
                name: m.name || m.title || '',
                percent: Number(m.percentage ?? m.percent ?? 0),
                due: m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : (m.due || ''),
              })),
            })));
        }

        if (resP.status === 'fulfilled') {
          const items = resP.value.data?.requests || resP.value.data?.data || [];
          setProformas(items
            .filter((r: any) => r.status !== 'cancelled')
            .map((r: any) => {
              const cleanPiNo = (r.proformaNumber || r.number || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim();
              return {
                id: r.id || r._id,
                type: 'proforma' as const,
                number: cleanPiNo || r.number || '',
                clientName: r.clientName || '',
                grandTotal: r.total || r.grandTotal || 0,
                sourceQuotationId: r.sourceQuotationId,
                sourceQuotationNumber: r.sourceQuotationNumber,
                proformaNumber: cleanPiNo || r.number || '',
                proformaRequestId: r.id || r._id,
                milestones: (r.milestones || r.template_data?.milestones || []).map((m: any) => ({
                  name: m.name || m.title || '',
                  percent: Number(m.percentage ?? m.percent ?? 0),
                  due: m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : (m.due || ''),
                })),
              };
            }));
        }
      } catch {
        setQuotations([]);
        setProformas([]);
      } finally {
        setLoadingDocs(false);
      }
    })();
  }, []);

  const pickDocument = (doc: ScheduleSourceOption) => {
    setSelected(doc);
    setTotalAmount(doc.grandTotal);
    setClientName(doc.clientName);
    const ms = doc.milestones.length > 0 ? doc.milestones : DEFAULT_MILESTONES;
    setMilestones(ms.map(m => ({ name: m.name, percent: m.percent, due: m.due, dueDate: '' })));
  };

  const updateMs = (idx: number, field: string, val: string | number) =>
    setMilestones(prev => prev.map((m, i) => i === idx ? { ...m, [field]: val } : m));
  const addMs = () => setMilestones(prev => [...prev, { name: '', percent: 0, due: '', dueDate: '' }]);
  const removeMs = (idx: number) => setMilestones(prev => prev.filter((_, i) => i !== idx));

  const totalPct = milestones.reduce((s, m) => s + (Number(m.percent) || 0), 0);

  const submit = async () => {
    if (!selected) { setErr(`Please select a ${sourceType === 'proforma' ? 'proforma invoice' : 'quotation'}.`); return; }
    if (!totalAmount || totalAmount <= 0) { setErr('Total amount must be > 0.'); return; }
    if (Math.abs(totalPct - 100) > 0.01) { setErr(`Percentages must sum to 100% (currently ${totalPct}%).`); return; }
    setSaving(true); setErr('');
    try {
      const isProforma = selected.type === 'proforma';
      await api.post('/api/accounting/payment-schedules', {
        quotationId: selected.sourceQuotationId || selected.id,
        quotationNumber: selected.sourceQuotationNumber || selected.number,
        proformaNumber: isProforma ? selected.number : (selected.proformaNumber || ''),
        proformaRequestId: isProforma ? selected.id : (selected.proformaRequestId || null),
        clientId: null,
        clientName,
        totalAmount,
        currency: 'INR',
        paymentType: 'milestone_based',
        milestones: milestones.map((m, i) => ({
          milestoneNumber: i + 1,
          name: m.name || `Milestone ${i + 1}`,
          description: m.due ? `Due: ${m.due}` : '',
          triggerType: 'manual',
          percentage: Number(m.percent) || 0,
          fixedAmount: 0,
          dueDate: m.dueDate ? new Date(m.dueDate).toISOString() : null,
        })),
      }, { skipErrorToast: true });
      onCreated();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'create this payment schedule');
      setErr(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Create Payment Schedule</h2>
            <p className="text-xs text-slate-500 mt-0.5">Link a proforma invoice or quotation and define milestone payment stages</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Schedule Source</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                type="button"
                onClick={() => { setSourceType('proforma'); setSelected(null); setErr(''); }}
                className={`py-2 px-3 rounded-lg text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${sourceType === 'proforma'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60'
                  }`}
              >
                <span>Proforma Invoice</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sourceType === 'proforma' ? 'bg-indigo-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  {proformas.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setSourceType('quotation'); setSelected(null); setErr(''); }}
                className={`py-2 px-3 rounded-lg text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${sourceType === 'quotation'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/60'
                  }`}
              >
                <span>Quotation</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sourceType === 'quotation' ? 'bg-indigo-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  {quotations.length}
                </span>
              </button>
            </div>

            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
              {sourceType === 'proforma' ? 'Select Proforma Invoice' : 'Select Quotation'}
            </label>
            {loadingDocs ? (
              <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading documents...</div>
            ) : (
              <select
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={selected?.id || ''}
                onChange={e => {
                  const currentList = sourceType === 'proforma' ? proformas : quotations;
                  const item = currentList.find(x => x.id === e.target.value);
                  if (item) pickDocument(item);
                }}
              >
                <option value="">
                  {sourceType === 'proforma' ? '— select a proforma invoice —' : '— select a quotation —'}
                </option>
                {(sourceType === 'proforma' ? proformas : quotations).map(d => (
                  <option key={d.id} value={d.id}>
                    {d.number} · {d.clientName} · {formatINR(d.grandTotal)}
                  </option>
                ))}
              </select>
            )}
          </div>
          {selected && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Contract Total (INR)</label>
                <input type="number" value={totalAmount} onChange={e => setTotalAmount(Number(e.target.value))}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Client Name</label>
                <input type="text" value={clientName} onChange={e => setClientName(e.target.value)}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              </div>
            </div>
          )}
          {selected && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Payment Milestones</label>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold ${Math.abs(totalPct - 100) > 0.01 ? 'text-red-600' : 'text-emerald-600'}`}>Total: {totalPct}%</span>
                  <button onClick={addMs} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-600 text-white text-[10px] font-semibold hover:bg-indigo-700">
                    <Plus className="w-3 h-3" /> Add Stage
                  </button>
                </div>
              </div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">#</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Stage Name</th>
                      <th className="px-3 py-2 text-center font-semibold text-slate-500">%</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-500">Amount</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Due / Trigger</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500">Due Date</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {milestones.map((m, i) => {
                      const amt = totalAmount > 0 ? Math.round(totalAmount * (m.percent / 100) * 100) / 100 : 0;
                      return (
                        <tr key={i} className="bg-white dark:bg-slate-900">
                          <td className="px-3 py-2 text-slate-400 font-bold">{i + 1}x</td>
                          <td className="px-3 py-2">
                            <input value={m.name} onChange={e => updateMs(i, 'name', e.target.value)} placeholder="Stage name"
                              className="w-full border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" value={m.percent} onChange={e => updateMs(i, 'percent', Number(e.target.value))}
                              className="w-14 text-center border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-[10px] text-slate-600 dark:text-slate-400 whitespace-nowrap">{formatINR(amt)}</td>
                          <td className="px-3 py-2">
                            <input value={m.due} onChange={e => updateMs(i, 'due', e.target.value)} placeholder="T + 15 days"
                              className="w-full border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="date" value={m.dueDate} onChange={e => updateMs(i, 'dueDate', e.target.value)}
                              className="w-36 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                          </td>
                          <td className="px-3 py-2">
                            <button onClick={() => removeMs(i)} className="p-0.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {err && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <XCircle className="w-3.5 h-3.5 flex-shrink-0" /> {err}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-600 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !selected || Math.abs(totalPct - 100) > 0.01}
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Allocate Customer Advance Modal ──────────────────────────────────────────

interface CustomerAdvanceItem {
  _id: string;
  advanceNo: string;
  balance?: number;
  balanceMinor?: number;
  amount?: number;
  amountMinor?: number;
  date?: string;
}

function getAdvBal(a: CustomerAdvanceItem): number {
  return a.balance !== undefined && a.balance !== null ? a.balance : ((a.balanceMinor || 0) / 100);
}

function getAdvAmt(a: CustomerAdvanceItem): number {
  return a.amount !== undefined && a.amount !== null ? a.amount : ((a.amountMinor || 0) / 100);
}

function AllocateAdvanceModal({
  scheduleId,
  milestone,
  onClose,
  onAllocated,
}: {
  scheduleId: string;
  milestone: Milestone;
  onClose: () => void;
  onAllocated: () => void;
}) {
  const [advances, setAdvances] = useState<CustomerAdvanceItem[]>([]);
  const [selectedAdvId, setSelectedAdvId] = useState<string>('');
  const [allocAmount, setAllocAmount] = useState<number>(milestone.amountOutstanding || milestone.amountDue);
  const [loading, setLoading] = useState(true);
  const [allocating, setAllocating] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    async function loadAdvances() {
      try {
        const res = await api.get(`/api/accounting/payment-schedules/${scheduleId}/milestones/${milestone._id}/customer-advances`);
        const list: CustomerAdvanceItem[] = res.data?.data || res.data || [];
        setAdvances(list);
        if (list.length > 0) {
          setSelectedAdvId(list[0]._id);
          const maxAvail = getAdvBal(list[0]);
          setAllocAmount(Math.min(maxAvail, milestone.amountOutstanding || milestone.amountDue));
        }
      } catch (e: any) {
        setErr('Failed to load customer advances.');
      } finally {
        setLoading(false);
      }
    }
    loadAdvances();
  }, [scheduleId, milestone._id]);

  const selectedAdv = advances.find(a => a._id === selectedAdvId);
  const maxAlloc = selectedAdv ? Math.min(getAdvBal(selectedAdv), milestone.amountOutstanding || milestone.amountDue) : 0;

  const handleAllocate = async () => {
    if (!selectedAdvId || allocAmount <= 0) return;
    setAllocating(true);
    try {
      await api.post(`/api/accounting/payment-schedules/${scheduleId}/milestones/${milestone._id}/allocate-advance`, {
        advanceId: selectedAdvId,
        amount: allocAmount,
      }, { skipErrorToast: true });
      onAllocated();
    } catch (e: any) {
      const errMsg = getApiErrorMessage(e, 'allocate this advance');
      setErr(errMsg);
    } finally {
      setAllocating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Allocate Customer Advance</h2>
            <p className="text-xs text-slate-500 mt-0.5">Apply customer advance liability to settlement of milestone</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-indigo-50 dark:bg-indigo-950/40 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900">
            <div className="text-xs font-bold text-indigo-900 dark:text-indigo-200">{milestone.name}</div>
            <div className="text-xs text-indigo-700 dark:text-indigo-400 mt-1 flex justify-between">
              <span>Milestone Outstanding:</span>
              <span className="font-bold">{formatINR(milestone.amountOutstanding || milestone.amountDue)}</span>
            </div>
          </div>

          {err && <div className="p-3 bg-red-50 text-red-700 text-xs rounded-lg font-medium">{err}</div>}

          {loading ? (
            <div className="flex items-center justify-center py-8 text-xs text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading customer advances...</div>
          ) : advances.length === 0 ? (
            <div className="p-4 bg-amber-50 text-amber-800 text-xs rounded-xl border border-amber-200 text-center font-medium">
              No unallocated customer advances found for this client.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Select Advance Note</label>
                <select
                  value={selectedAdvId}
                  onChange={e => {
                    setSelectedAdvId(e.target.value);
                    const adv = advances.find(a => a._id === e.target.value);
                    if (adv) {
                      setAllocAmount(Math.min(getAdvBal(adv), milestone.amountOutstanding || milestone.amountDue));
                    }
                  }}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                >
                  {advances.map(a => (
                    <option key={a._id} value={a._id}>
                      {a.advanceNo} · Available: {formatINR(getAdvBal(a))} (Total: {formatINR(getAdvAmt(a))})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex justify-between text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                  <span>Allocation Amount (INR)</span>
                  <span className="text-slate-400">Max: {formatINR(maxAlloc)}</span>
                </div>
                <input
                  type="number"
                  min="1"
                  max={maxAlloc}
                  value={allocAmount}
                  onChange={e => setAllocAmount(Math.min(Number(e.target.value), maxAlloc))}
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 font-mono"
                />
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={handleAllocate}
            disabled={allocating || advances.length === 0 || allocAmount <= 0}
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {allocating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Allocate Advance
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Record Customer Payment Modal ──────────────────────────────────────────

/** Opens a payment's proof (receipt or transaction screenshot) in a new tab. */
async function openPaymentProof(scheduleId: string, milestoneId: string, paymentId: string) {
  try {
    const res = await api.get(`/api/accounting/payment-schedules/${scheduleId}/milestones/${milestoneId}/payments/${paymentId}/proof`);
    const dataUrl: string = res.data?.fileData || '';
    const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
    if (!match) return;
    const bytes = Uint8Array.from(atob(match[2]), c => c.charCodeAt(0));
    window.open(URL.createObjectURL(new Blob([bytes], { type: match[1] })), '_blank');
  } catch (e: any) {
    alert(getApiErrorMessage(e, 'open this proof'));
  }
}

function PaymentHistoryModal({
  schedule,
  milestone,
  onClose,
}: {
  schedule: Schedule;
  milestone: Milestone;
  onClose: () => void;
}) {
  const payments = milestone.payments || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" /> Payments received
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{schedule.clientName} · {milestone.name} · {milestone.proformaNumber || schedule.proformaNumber || ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {payments.length === 0 ? (
            <p className="text-xs text-slate-500">No payments have been recorded yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="py-2 text-left">Date</th>
                  <th className="py-2 text-right">Amount</th>
                  <th className="py-2 text-left pl-4">Mode / Reference</th>
                  <th className="py-2 text-left">Recorded by</th>
                  <th className="py-2 text-right">Proof</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {payments.map(p => (
                  <tr key={p._id}>
                    <td className="py-2 whitespace-nowrap">{p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="py-2 text-right font-mono font-bold text-emerald-600">{formatINR(p.amount)}</td>
                    <td className="py-2 pl-4">
                      <div className="font-medium">{PAYMENT_MODE_LABELS[p.paymentMode || ''] || p.paymentMode || '—'}</div>
                      {(p.referenceNo || p.note) && <div className="text-slate-500">{[p.referenceNo, p.note].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td className="py-2 whitespace-nowrap">{p.recordedByName || '—'}</td>
                    <td className="py-2 text-right">
                      {p.proofId ? (
                        <button onClick={() => openPaymentProof(schedule._id, milestone._id, p._id)} className="text-indigo-600 hover:underline font-semibold cursor-pointer">
                          {p.proofFileName || 'View'}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 dark:border-slate-700 font-bold">
                  <td className="py-2">Total received</td>
                  <td className="py-2 text-right font-mono text-emerald-600">{formatINR(milestone.amountReceived || 0)}</td>
                  <td colSpan={3} className="py-2 pl-4 text-slate-500 font-normal">Outstanding {formatINR(milestone.amountOutstanding || 0)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function RecordPaymentModal({
  schedule,
  milestone,
  onClose,
  onRecorded,
}: {
  schedule: Schedule;
  milestone: Milestone;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const outstanding = milestone.amountOutstanding || milestone.amountDue || 0;
  const totalAmount = milestone.amountDue || 0;
  const receivedAmount = milestone.amountReceived || 0;
  const [payAmount, setPayAmount] = useState<number>(outstanding);
  const [paymentMode, setPaymentMode] = useState<string>('bank_transfer');
  const [referenceNo, setReferenceNo] = useState<string>('');
  const [paidAt, setPaidAt] = useState<string>(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState<string>('');
  const [proofFileName, setProofFileName] = useState<string>('');
  const [proofFileData, setProofFileData] = useState<string>('');
  const [recording, setRecording] = useState(false);
  const [err, setErr] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const dueDateStr = milestone.dueDate
    ? new Date(milestone.dueDate).toLocaleDateString('en-IN')
    : 'On Signing';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErr('Receipt file size must be less than 5MB');
      return;
    }

    setErr('');
    setProofFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      setProofFileData(event.target?.result as string || '');
    };
    reader.readAsDataURL(file);
  };

  const removeProof = () => {
    setProofFileName('');
    setProofFileData('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRecord = async () => {
    if (payAmount <= 0) return;
    setRecording(true);
    try {
      await api.post(`/api/accounting/payment-schedules/${schedule._id}/milestones/${milestone._id}/record-payment`, {
        amount: payAmount,
        paymentMode,
        referenceNo,
        paidAt,
        note,
        proofAttachment: proofFileData ? {
          fileName: proofFileName,
          fileData: proofFileData,
        } : null,
      }, { skipErrorToast: true });
      onRecorded();
    } catch (e: any) {
      const errMsg = getApiErrorMessage(e, 'record this payment');
      setErr(errMsg);
    } finally {
      setRecording(false);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              Record Customer Payment
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Record a payment received against this milestone (advance on the approved proforma, or against its tax invoice)</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Tax Invoice & Milestone Details Card */}
          <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2.5">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{milestone.invoiceNumber ? 'Invoice Ref' : 'Proforma Ref'}</span>
                <div className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">
                  {milestone.invoiceNumber || milestone.proformaNumber || schedule.proformaNumber || '—'}
                </div>
              </div>
              <div className="text-right">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Quotation Ref</span>
                <div className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {schedule.quotationNumber || '—'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-400 font-medium">Customer (Bill To):</span>
                <div className="font-bold text-slate-800 dark:text-slate-200 truncate">
                  {schedule.clientName || 'Customer'} {schedule.clientCode ? `(${schedule.clientCode})` : (schedule.clientId && !/^[0-9a-fA-F]{24}$/.test(String(schedule.clientId)) ? `(${schedule.clientId})` : '')}
                </div>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Milestone Phase:</span>
                <div className="font-bold text-slate-800 dark:text-slate-200">
                  Phase {milestone.milestoneNumber} ({milestone.percentage}%)
                </div>
              </div>
              <div className="col-span-2">
                <span className="text-slate-400 font-medium">Milestone Stage Name:</span>
                <div className="font-bold text-slate-900 dark:text-slate-100">{milestone.name}</div>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Milestone Due Date:</span>
                <div className="font-bold text-amber-700 dark:text-amber-400">{dueDateStr}</div>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Invoice Status:</span>
                <div className="font-semibold text-emerald-700 dark:text-emerald-400 capitalize">{milestone.status.replace('_', ' ')}</div>
              </div>
            </div>

            {/* Financial Summary Box */}
            <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3 rounded-lg border border-emerald-200 dark:border-emerald-900 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Milestone Amount:</span>
                <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">{formatINR(totalAmount)}</span>
              </div>
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Already Received:</span>
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">{formatINR(receivedAmount)}</span>
              </div>
              <div className="flex justify-between text-emerald-900 dark:text-emerald-200 font-bold border-t border-emerald-200 dark:border-emerald-800 pt-1.5 text-sm">
                <span>Outstanding:</span>
                <span className="font-mono">{formatINR(outstanding)}</span>
              </div>
            </div>
          </div>

          {err && <div className="p-3 bg-red-50 text-red-700 text-xs rounded-lg font-medium">{err}</div>}

          {/* Payment Amount Field */}
          <div>
            <div className="flex justify-between text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              <span>Payment Amount (INR)</span>
              <span className="text-slate-400 font-mono">Max: {formatINR(outstanding)}</span>
            </div>
            <input
              type="number"
              min="1"
              max={outstanding}
              value={payAmount}
              onChange={e => setPayAmount(Math.min(Number(e.target.value), outstanding))}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono font-bold"
            />
          </div>

          {/* Payment Mode Field */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Payment Method</label>
              <select
                value={paymentMode}
                onChange={e => setPaymentMode(e.target.value)}
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-xs"
              >
                <option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</option>
                <option value="upi">UPI / GPay / PhonePe</option>
                <option value="cheque">Cheque / Demand Draft</option>
                <option value="cash">Cash Receipt</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Ref / UTR / Cheque No.</label>
              <input
                type="text"
                placeholder="Txn ID / UTR Number"
                value={referenceNo}
                onChange={e => setReferenceNo(e.target.value)}
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-xs"
              />
            </div>
          </div>

          {/* Payment date and note */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Payment Date</label>
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={paidAt}
                onChange={e => setPaidAt(e.target.value)}
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-xs"
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Note <span className="text-slate-400 font-normal">(Optional)</span></label>
              <input
                type="text"
                placeholder="e.g. Advance against proforma"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-xs"
              />
            </div>
          </div>

          {/* Upload Transaction Proof / Receipt */}
          <div className="space-y-1.5 text-xs">
            <label className="block font-semibold text-slate-700 dark:text-slate-300">
              Upload Transaction Proof / Receipt <span className="text-slate-400 font-normal">(Optional — Slip, Screenshot, PDF)</span>
            </label>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />
            {proofFileName ? (
              <div className="flex items-center justify-between p-2.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-semibold text-emerald-900 dark:text-emerald-200 truncate">{proofFileName}</span>
                </div>
                <button
                  type="button"
                  onClick={removeProof}
                  className="p-1 text-slate-400 hover:text-red-500 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  title="Remove proof attachment"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 rounded-lg bg-slate-50/50 dark:bg-slate-800/40 cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 font-medium">
                  <Upload className="w-4 h-4" />
                  <span>Click to upload transaction proof or receipt</span>
                </div>
                <span className="text-[10px] text-slate-400 mt-0.5">Supports PNG, JPG, JPEG, or PDF up to 5MB</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={handleRecord}
            disabled={recording || payAmount <= 0}
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {recording ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Record Payment
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Interactive Editable Invoice Drawer ───────────────────────────────────────

interface InvoiceDrawerProps {
  html: string;
  title: string;
  invoiceRequestId?: string | null;
  isPaid?: boolean;
  canGenerate?: boolean;
  generateBtnText?: string;
  onClose: () => void;
  onSave?: (editedHtml: string) => Promise<void>;
  onGenerate?: (editedHtml: string) => Promise<void>;
  /** Called after a PDF of this document has been saved, so the issuer can record the copy. */
  onDownloaded?: () => Promise<void>;
}

function InvoiceDrawer({
  html, title, isPaid, canGenerate, generateBtnText, onClose, onSave, onGenerate, onDownloaded,
}: InvoiceDrawerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const executeIframeAction = (action: 'addRow' | 'addMilestone' | 'addNote' | 'addTerm') => {
    if (!iframeRef.current || !iframeRef.current.contentDocument) return;
    const doc = iframeRef.current.contentDocument;
    const win = doc.defaultView as any;

    if (action === 'addRow') {
      if (win && typeof win.addRow === 'function') {
        win.addRow();
      } else {
        let b = doc.getElementById('itemsBody') || doc.querySelector('#items tbody') || doc.querySelector('table tbody');
        if (!b) {
          const sec = doc.createElement('div');
          sec.innerHTML = `<div class="sec" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap"><h2>Particulars</h2><div style="display:flex;align-items:center;gap:6px" class="item-actions"><select id="catalogItemSelect" class="btn-add-inline-select" onchange="window.selectCatalogItem ? window.selectCatalogItem(this) : selectCatalogItem(this)"><option value="">+ Select Item to Add...</option><option value="__custom__">+ Add Custom / Blank Item</option></select><button type="button" class="btn-add-inline" onclick="window.addRow ? window.addRow() : addRow()">+ Particulars / Item</button></div></div><table id="items"><colgroup><col style="width:22px"/><col/><col class="hsncol" style="width:60px"/><col style="width:32px"/><col style="width:34px"/><col style="width:56px"/><col style="width:34px"/><col style="width:74px"/><col style="width:32px"/><col style="width:74px"/></colgroup><thead><tr><th class="c">#</th><th>Description of goods / services</th><th class="hsncol c">HSN/SAC</th><th class="n">Qty</th><th class="c">Unit</th><th class="n">Rate</th><th class="n">Disc%</th><th class="n">Taxable</th><th class="n">GST%</th><th class="n">Tax amt</th></tr></thead><tbody id="itemsBody"></tbody></table>`;
          const hdr = doc.querySelector('.hdr') || doc.querySelector('.info');
          if (hdr && hdr.parentNode) hdr.parentNode.insertBefore(sec, hdr.nextSibling);
          else (doc.querySelector('.body') || doc.body).appendChild(sec);
          b = doc.getElementById('itemsBody');
        }
        if (b) {
          const tr = doc.createElement('tr');
          tr.innerHTML = `<td class="c">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumber)window.renumber();if(window.recalc)window.recalc()">×</button></td><td><div class="it-name" contenteditable data-ph="Item / service">New Item / Service</div><div class="it-desc" contenteditable data-ph="Details (optional)"></div></td><td class="hsncol c" contenteditable data-ph="—">998314</td><td class="n" contenteditable>1</td><td class="c" contenteditable data-ph="—">Nos</td><td class="n" contenteditable>0</td><td class="n" contenteditable>0</td><td class="n calc" data-taxable>—</td><td class="n" contenteditable>18</td><td class="n calc" data-tax>—</td></tr>`;
          b.appendChild(tr);
          if (win && typeof win.renumber === 'function') win.renumber();
          if (win && typeof win.recalc === 'function') win.recalc();
        }
      }
    } else if (action === 'addMilestone') {
      if (win && typeof win.addMilestone === 'function') {
        win.addMilestone();
      } else {
        let b = doc.getElementById('schedBody') || doc.querySelector('.msched tbody');
        if (!b) {
          const sec = doc.createElement('div');
          sec.id = 'schedOptSection';
          sec.setAttribute('data-opt', 'sched');
          sec.innerHTML = `<div class="sec" style="margin-top:14px;display:flex;align-items:center;justify-content:space-between"><h2>Payment Schedule</h2></div><table class="msched"><colgroup><col style="width:22px"/><col/><col style="width:52px"/><col style="width:80px"/><col style="width:76px"/></colgroup><thead><tr><th class="c">#</th><th>Milestone / Stage</th><th class="n">% of Total</th><th class="n">Amount (Incl. GST)</th><th class="n">Due / Trigger</th></tr></thead><tbody id="schedBody"></tbody></table>`;
          const totWrap = doc.querySelector('.tot-wrap') || doc.querySelector('#totals')?.closest('.tot-wrap');
          if (totWrap && totWrap.parentNode) totWrap.parentNode.insertBefore(sec, totWrap.nextSibling);
          else (doc.querySelector('.body') || doc.body).appendChild(sec);
          b = doc.getElementById('schedBody');
        }
        if (b) {
          const tr = doc.createElement('tr');
          tr.innerHTML = `<td class="c">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumberSched)window.renumberSched();if(window.recalc)window.recalc()">×</button></td><td contenteditable data-ph="Stage name">New Stage / Milestone</td><td class="n" contenteditable>0</td><td class="n calc" data-msamt>—</td><td class="n" contenteditable data-ph="On signing / T+30">T+30 days</td></tr>`;
          b.appendChild(tr);
          if (win && typeof win.renumberSched === 'function') win.renumberSched();
          if (win && typeof win.recalc === 'function') win.recalc();
        }
      }
    } else if (action === 'addNote') {
      if (win && typeof win.addNote === 'function') {
        win.addNote();
      } else {
        let notes = doc.getElementById('notes');
        if (!notes) {
          let leftcol = doc.querySelector('.leftcol');
          if (!leftcol) {
            const totWrap = doc.querySelector('.tot-wrap') || doc.querySelector('#totals')?.closest('.tot-wrap');
            if (totWrap) {
              leftcol = doc.createElement('div');
              leftcol.className = 'leftcol';
              totWrap.insertBefore(leftcol, totWrap.firstChild);
            }
          }
          if (leftcol) {
            const box = doc.createElement('div');
            box.className = 'words';
            box.innerHTML = '<div class="lbl" style="display:flex;align-items:center;justify-content:space-between"><span>Notes</span><button type="button" class="btn-add-inline-sm" onclick="window.addNote ? window.addNote() : addNote()">+ Note</button></div><ul class="list" id="notes" contenteditable></ul>';
            leftcol.appendChild(box);
            notes = doc.getElementById('notes');
          }
        }
        if (notes) {
          const li = doc.createElement('li');
          li.contentEditable = 'true';
          li.textContent = 'New note...';
          notes.appendChild(li);
          li.focus();
        }
      }
    } else if (action === 'addTerm') {
      if (win && typeof win.addTerm === 'function') {
        win.addTerm();
      } else {
        let terms = doc.getElementById('terms');
        if (!terms) {
          const container = doc.querySelector('.body') || doc.body;
          const box = doc.createElement('div');
          box.innerHTML = '<div class="words"><div class="lbl" style="display:flex;align-items:center;justify-content:space-between"><span>Terms &amp; Conditions (Payment terms, delivery timeline, warranty...)</span><button type="button" class="btn-add-inline-sm" onclick="window.addTerm ? window.addTerm() : addTerm()">+ Term / Condition</button></div><ul class="list" id="terms" contenteditable></ul></div>';
          const cols = doc.querySelector('.cols');
          if (cols && cols.parentNode) cols.parentNode.insertBefore(box, cols);
          else container.appendChild(box);
          terms = doc.getElementById('terms');
        }
        if (terms) {
          const li = doc.createElement('li');
          li.contentEditable = 'true';
          li.textContent = 'Payment terms, delivery timeline, warranty...';
          terms.appendChild(li);
          li.focus();
        }
      }
    }
  };

  // The document is a preview only: it is never edited in place. Changes are made in the forms
  // (quotation form, proforma form); a tax invoice is built from its approved proforma.
  const handleIframeLoad = () => {
    if (iframeRef.current && iframeRef.current.contentDocument) {
      try {
        const doc = iframeRef.current.contentDocument;
        if (doc.body) {
          doc.body.contentEditable = 'false';
          doc.designMode = 'off';
          doc.querySelectorAll('[contenteditable]').forEach(el => {
            el.removeAttribute('contenteditable');
          });
          // Row delete / add buttons and the add-row toolbar have no place in a preview.
          doc.querySelectorAll('.rowdel, .btn-add, .row-actions, .btn-add-inline, .btn-add-inline-sm, .btn-add-inline-select').forEach(el => el.remove());
          doc.querySelectorAll('button[onclick*="addRow"], button[onclick*="addNote"], button[onclick*="addTerm"], button[onclick*="addMilestone"]').forEach(el => el.remove());
          doc.querySelectorAll('select, input, textarea').forEach(el => {
            (el as HTMLInputElement).disabled = true;
          });
        }

        // Attach click interceptor inside iframe document to capture toolbar action buttons
        doc.addEventListener('click', (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          if (!target) return;

          const btn = target.closest('button');
          if (!btn) return;
          const onclickAttr = btn.getAttribute('onclick') || '';
          const btnText = (btn.textContent || '').trim();
          if (onclickAttr.includes('addRow') || btnText.includes('Particulars') || btnText.includes('Line item')) {
            e.preventDefault(); e.stopPropagation();
            executeIframeAction('addRow');
          } else if (onclickAttr.includes('addMilestone') || btnText.includes('Stage') || btnText.includes('Schedule')) {
            e.preventDefault(); e.stopPropagation();
            executeIframeAction('addMilestone');
          } else if (onclickAttr.includes('addNote') || btnText.includes('Note')) {
            e.preventDefault(); e.stopPropagation();
            executeIframeAction('addNote');
          } else if (onclickAttr.includes('addTerm') || btnText.includes('Term') || btnText.includes('Condition')) {
            e.preventDefault(); e.stopPropagation();
            executeIframeAction('addTerm');
          }
        }, true);
      } catch { }
    }
  };

  useEffect(() => {
    handleIframeLoad();
  }, [html, isPaid]);

  const getEditedHtml = (): string => {
    if (iframeRef.current && iframeRef.current.contentDocument) {
      try {
        const doc = iframeRef.current.contentDocument;
        doc.querySelectorAll('input').forEach((inp: any) => {
          if (inp.type === 'checkbox' || inp.type === 'radio') {
            if (inp.checked) inp.setAttribute('checked', 'checked');
            else inp.removeAttribute('checked');
          } else {
            inp.setAttribute('value', inp.value);
          }
        });
        doc.querySelectorAll('textarea').forEach((ta: any) => {
          ta.textContent = ta.value;
        });
        doc.querySelectorAll('select').forEach((sel: any) => {
          sel.querySelectorAll('option').forEach((opt: any) => {
            if (opt.selected) opt.setAttribute('selected', 'selected');
            else opt.removeAttribute('selected');
          });
        });
        return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
      } catch { }
    }
    return html;
  };

  const handleSave = async () => {
    if (!onSave || isPaid) return;
    setSaving(true);
    try {
      const currentHtml = getEditedHtml();
      await onSave(currentHtml);
    } finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!onGenerate || isPaid) return;
    setGenerating(true);
    try {
      const currentHtml = getEditedHtml();
      await onGenerate(currentHtml);
    } finally { setGenerating(false); }
  };

  // The Download button inside the document asks this drawer to save it; the server makes the PDF.
  const handleDownload = async (name?: string) => {
    setDownloadError(null);
    try {
      await downloadHtmlAsPdf(getEditedHtml(), pdfFileName(name || title, 'document'));
      await onDownloaded?.();
    } catch {
      setDownloadError('Could not make the PDF. Please try again.');
    }
  };

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow || e.data?.type !== 'zynx-download-pdf') return;
      void handleDownload(typeof e.data.name === 'string' ? e.data.name : undefined);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleUploadLogoToDrawer = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) {
          try { localStorage.setItem('dt_company_logo', dataUrl); } catch { }
          if (iframeRef.current && iframeRef.current.contentDocument) {
            const doc = iframeRef.current.contentDocument;
            const slot = doc.getElementById('logoSlot');
            if (slot) {
              slot.innerHTML = `<img src="${dataUrl}" style="max-height:100%;max-width:100%;object-fit:contain"/>`;
            }
          }
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-5xl bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <div className="min-w-0 pr-4">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate block">{title}</span>
              {isPaid && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 flex items-center gap-1">
                  <CheckCheck className="w-3 h-3 text-emerald-600" /> Paid Invoice
                </span>
              )}
            </div>
            <span className="text-[11px] text-slate-500 block mt-0.5">
              {isPaid ? (
                <span className="flex items-center gap-1 text-slate-500 font-medium">
                  <Lock className="w-3 h-3 text-slate-400" /> Paid Invoice — Read-only view (Editing and generation disabled)
                </span>
              ) : (
                <span className="flex items-center gap-1 text-slate-500 font-medium">
                  <Lock className="w-3 h-3 text-slate-400" /> Read-only preview. To change anything, edit the quotation or proforma it comes from.
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {downloadError && <span role="alert" className="text-[11px] font-semibold text-red-600">{downloadError}</span>}
            {!isPaid && canGenerate && onGenerate && (
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 shadow-sm"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                {generateBtnText || 'Generate & Save Invoice'}
              </button>
            )}

            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* HTML Canvas */}
        <iframe
          ref={iframeRef}
          srcDoc={html}
          onLoad={handleIframeLoad}
          className="flex-1 w-full border-0 bg-slate-100 dark:bg-slate-950"
          title={title}
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function MilestoneDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: masterData } = useDocumentMasterData();
  const { data: docNumberingList } = useDocumentNumbering({ isActive: true });
  const activeDocNumbering = docNumberingList?.[0];
  const defaultBankDetail = (masterData?.bankDetails ?? []).find((b) => b.isDefault && b.isActive);
  const { data: companyProfile } = useCompanyProfile();

  // ── Document number formatter using master data config ─────────────────────
  const numberFormat = (activeDocNumbering?.quotation?.numberFormat || masterData?.quotationDefaults?.numberFormat || 'DT-{PREFIX}-{FY}/{NUMBER}').trim();
  const padding = activeDocNumbering?.quotation?.padding ?? masterData?.quotationDefaults?.padding ?? 4;
  const defaultFy = normalizeFiscalYear(activeDocNumbering?.quotation?.fiscalYear || masterData?.quotationDefaults?.fiscalYear || '');

  const fmtQuotation = useCallback((raw?: string) =>
    applyNumberFormat(raw, { numberFormat, padding, fiscalYear: defaultFy, targetPrefix: activeDocNumbering?.quotation?.prefix || 'QT' }),
    [activeDocNumbering, numberFormat, padding, defaultFy]
  );
  const fmtProforma = useCallback((raw?: string) =>
    applyNumberFormat(raw, { numberFormat, padding, fiscalYear: defaultFy, targetPrefix: activeDocNumbering?.proforma?.prefix || 'PI' }),
    [activeDocNumbering, numberFormat, padding, defaultFy]
  );
  // A running bill number (DT/RB/2026-27/001) is issued already formatted from its own series, so it is shown as it is.
  const runningBillPrefix = (activeDocNumbering?.runningBill?.prefix || 'RB').toUpperCase();
  const fmtInvoice = useCallback((raw?: string) => {
    const clean = String(raw ?? '').trim();
    if (new RegExp(`^(?:DT[-/])?${runningBillPrefix}[-_/]\\d{4}`, 'i').test(clean)) return clean;
    return applyNumberFormat(raw, { numberFormat, padding, fiscalYear: defaultFy, targetPrefix: activeDocNumbering?.taxInvoice?.prefix || 'TI' });
  }, [activeDocNumbering, numberFormat, padding, defaultFy, runningBillPrefix]);
  // The saved document does not store the company's state; it is read from the live company profile.
  const companyState: string = (companyProfile as any)?.registeredAddress?.state || '';
  const targetQuot = searchParams.get('quotationNumber') || searchParams.get('quotationId') || searchParams.get('scheduleId');
  const backUrl = (location.state as any)?.from || searchParams.get('from') || searchParams.get('returnUrl') || '';

  const currentUser = useUser();
  const isSuperAdminOrOwner = useMemo(() => {
    // Only users who are Superadmin (i.e. uzef@durranitech.com, department Superadmin, or role superadmin/owner)
    const dept = (currentUser?.department || '').toLowerCase();
    const role = (currentUser?.role || '').toLowerCase();
    const email = (currentUser?.email || '').toLowerCase();
    return dept === 'superadmin' || role === 'superadmin' || role === 'owner' || email.includes('uzef');
  }, [currentUser]);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [dismissedBanner, setDismissedBanner] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [preparedByFilter, setPreparedByFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [drawerMeta, setDrawerMeta] = useState<{
    html: string;
    title: string;
    sch: Schedule;
    ms: Milestone;
    idx: number;
    canGenerate?: boolean;
    isProforma?: boolean;
    finalInvoiceRequestId?: string;
  } | null>(null);
  const [allocMeta, setAllocMeta] = useState<{ sch: Schedule; ms: Milestone } | null>(null);
  const [payMeta, setPayMeta] = useState<{ sch: Schedule; ms: Milestone } | null>(null);
  const [historyMeta, setHistoryMeta] = useState<{ sch: Schedule; ms: Milestone } | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null);

  // ── Milestone Reminder state ──────────────────────────────────────────────
  const [reminderModal, setReminderModal] = useState<{ open: boolean; sch: Schedule; ms: Milestone } | null>(null);
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderPreset, setReminderPreset] = useState<'tomorrow_morning' | 'in_3_days' | 'on_due_date' | 'custom'>('on_due_date');
  const [reminderCustomDate, setReminderCustomDate] = useState('');
  const [reminderNotes, setReminderNotes] = useState('');
  const [creatingReminder, setCreatingReminder] = useState(false);

  const showToast = (msg: any, type: 'ok' | 'err' = 'ok') => {
    const text = typeof msg === 'string'
      ? msg
      : (typeof msg?.message === 'string' ? msg.message : (typeof msg?.error === 'string' ? msg.error : (typeof msg?.error?.message === 'string' ? msg.error.message : String(msg || 'Notice'))));
    setToast({ msg: text, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDownloadReceipt = async (params: {
    schedule: Schedule;
    milestone: Milestone;
    receiptNo: string;
    journalNo: string;
    journalDate: string;
    customerName: string;
    customerCode: string | null;
    narration: string;
    totalDebit: number;
    paymentMode: string;
    referenceNo: string;
  }) => {
    // Build the receipt page and download it as a PDF
    const { schedule, milestone, receiptNo, journalDate, customerName, customerCode, narration, totalDebit, paymentMode, referenceNo } = params;

    const receiptHtml = buildReceiptHtml({
      company: companyProfile as any,
      logo: (typeof window !== 'undefined' ? (localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo') || '') : '') || undefined,
      receiptNo,
      date: journalDate,
      customerName,
      customerCode,
      quotationNumber: schedule.quotationNumber,
      againstNumber: milestone.invoiceNumber || milestone.proformaNumber || schedule.proformaNumber || '',
      milestoneName: milestone.name,
      percentage: milestone.percentage,
      narration,
      amountPaise: totalDebit,
      paymentMode,
      referenceNo,
    });

    try {
      await downloadHtmlAsPdf(receiptHtml, pdfFileName(`Receipt_${receiptNo}`));
    } catch {
      showToast('Failed to download the receipt', 'err');
    }
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/accounting/payment-schedules?limit=100');
      const items: Schedule[] = Array.isArray(res.data?.schedules)
        ? res.data.schedules
        : Array.isArray(res.data?.data?.items)
          ? res.data.data.items
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data?.items)
              ? res.data.items
              : Array.isArray(res.data)
                ? res.data
                : [];
      setSchedules(items);
      if (items.length === 1 && items[0]?._id) {
        setExpanded({ [items[0]._id]: true });
      } else if (targetQuot) {
        const qLower = targetQuot.toLowerCase();
        const found = items.find(s =>
          s.quotationNumber?.toLowerCase() === qLower ||
          s.quotationId === targetQuot ||
          s._id === targetQuot ||
          s.clientName?.toLowerCase().includes(qLower) ||
          (s as any).invoiceNumber?.toLowerCase() === qLower ||
          (s as any).baseInvoiceNumber?.toLowerCase() === qLower ||
          (s as any).proformaNumber?.toLowerCase() === qLower
        );
        if (found && found._id) {
          setExpanded(prev => ({ ...prev, [found._id]: true }));
        }
      }
    } catch { setSchedules([]); }
    finally { setLoading(false); }
  }, [targetQuot]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  useEffect(() => {
    if (targetQuot && schedules.length > 0) {
      const qLower = targetQuot.toLowerCase();
      const found = schedules.find(s =>
        s.quotationNumber?.toLowerCase() === qLower ||
        s.quotationId === targetQuot ||
        s._id === targetQuot ||
        s.clientName?.toLowerCase().includes(qLower) ||
        (s as any).invoiceNumber?.toLowerCase() === qLower ||
        (s as any).baseInvoiceNumber?.toLowerCase() === qLower ||
        (s as any).proformaNumber?.toLowerCase() === qLower
      );
      if (found && found._id) {
        setExpanded(prev => ({ ...prev, [found._id]: true }));
        focusSchedule(found._id);
      }
    }
  }, [targetQuot, schedules]);

  const upcomingMilestones = useMemo(() => {
    const list: Array<{ sch: Schedule; ms: Milestone; daysDiff: number }> = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    (schedules || []).forEach(sch => {
      (sch.milestones || []).forEach(ms => {
        if (['pending', 'due'].includes(ms.status) && ms.dueDate) {
          const due = new Date(ms.dueDate);
          due.setHours(0, 0, 0, 0);
          const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          if (diff <= 7) {
            list.push({ sch, ms, daysDiff: diff });
          }
        }
      });
    });
    return list.sort((a, b) => a.daysDiff - b.daysDiff);
  }, [schedules]);

  const focusSchedule = (schId: string) => {
    setExpanded(prev => ({ ...prev, [schId]: true }));
    setTimeout(() => {
      const el = document.getElementById(`sch-${schId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2'), 2500);
      }
    }, 100);
  };

  // Distinct sales reps who prepared at least one listed schedule.
  const preparedByOptions = useMemo(
    () => Array.from(new Set(schedules.map(schedulePreparedBy).filter(Boolean))).sort() as string[],
    [schedules]
  );

  const filteredSchedules = useMemo(() => {
    let result = [...schedules];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      // quotationId / clientId are ObjectId refs and quotationId arrives populated as a full
      // document object — stringify defensively or .toLowerCase() throws and crashes the page.
      const idToStr = (v: any): string =>
        v == null ? '' : (typeof v === 'string' ? v : String(v._id ?? v));
      result = result.filter(sch => {
        const matchesClient = sch.clientName?.toLowerCase().includes(term) || (sch.clientCode && sch.clientCode.toLowerCase().includes(term)) || idToStr(sch.clientId).toLowerCase().includes(term);
        const matchesQuot = sch.quotationNumber?.toLowerCase().includes(term) || idToStr(sch.quotationId).toLowerCase().includes(term);
        const matchesBaseInv = (sch as any).baseInvoiceNumber?.toLowerCase().includes(term) || (sch as any).invoiceNumber?.toLowerCase().includes(term) || (sch as any).proformaNumber?.toLowerCase().includes(term);
        const matchesMilestone = sch.milestones?.some(m =>
          m.name?.toLowerCase().includes(term) ||
          m.invoiceNumber?.toLowerCase().includes(term) ||
          m.proformaNumber?.toLowerCase().includes(term) ||
          m.receiptNo?.toLowerCase().includes(term)
        );
        // Also match the populated quotation document's own number, if present
        const matchesPopulatedQuot = sch.quotationId && typeof sch.quotationId === 'object'
          ? Boolean((sch.quotationId as any).number?.toLowerCase().includes(term))
          : false;
        return matchesClient || matchesQuot || matchesBaseInv || matchesMilestone || matchesPopulatedQuot;
      });
    }

    // Prepared-by filter (the salesperson/assignee behind the quotation)
    if (preparedByFilter !== 'all') {
      result = result.filter(sch => schedulePreparedBy(sch) === preparedByFilter);
    }

    // Schedule status filter
    if (statusFilter !== 'all') {
      result = result.filter(sch => sch.status === statusFilter);
    }

    // Payment type filter
    if (paymentTypeFilter !== 'all') {
      if (paymentTypeFilter === 'full_payment') {
        result = result.filter(sch => sch.paymentType === 'full_payment' || (!sch.milestones?.length) || (sch.milestones?.length === 1 && sch.milestones[0].percentage === 100));
      } else if (paymentTypeFilter === 'milestone_based') {
        result = result.filter(sch => sch.paymentType !== 'full_payment' && (sch.milestones?.length || 0) > 1);
      }
    }

    // Stage filter
    if (stageFilter !== 'all') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      result = result.filter(sch => {
        const msList = sch.milestones || [];
        if (stageFilter === 'has_overdue') {
          return msList.some(m => ['pending', 'due'].includes(m.status) && m.dueDate && new Date(m.dueDate).getTime() < now.getTime());
        }
        if (stageFilter === 'has_due') {
          return msList.some(m => m.status === 'due');
        }
        if (stageFilter === 'has_pending') {
          return msList.some(m => m.status === 'pending');
        }
        if (stageFilter === 'has_invoiced') {
          return msList.some(m => ['invoice_generated', 'proforma_approved', 'proforma_pending_approval'].includes(m.status));
        }
        if (stageFilter === 'has_partially_paid') {
          return msList.some(m => m.status === 'partially_paid');
        }
        if (stageFilter === 'fully_paid') {
          return sch.status === 'completed' || (msList.length > 0 && msList.every(m => m.status === 'paid'));
        }
        return true;
      });
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      }
      if (sortBy === 'oldest') {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateA - dateB;
      }
      if (sortBy === 'highest_value') {
        return (b.totalAmount || 0) - (a.totalAmount || 0);
      }
      if (sortBy === 'lowest_value') {
        return (a.totalAmount || 0) - (b.totalAmount || 0);
      }
      if (sortBy === 'client_az') {
        return (a.clientName || '').localeCompare(b.clientName || '');
      }
      if (sortBy === 'progress_asc') {
        return (a.paymentProgress || 0) - (b.paymentProgress || 0);
      }
      return 0;
    });

    return result;
  }, [schedules, searchTerm, statusFilter, paymentTypeFilter, stageFilter, preparedByFilter, sortBy]);

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    filteredSchedules.forEach(s => { next[s._id] = true; });
    setExpanded(next);
  };

  const collapseAll = () => {
    setExpanded({});
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPaymentTypeFilter('all');
    setStageFilter('all');
    setPreparedByFilter('all');
    setSortBy('newest');
  };

  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter !== 'all' || paymentTypeFilter !== 'all' || stageFilter !== 'all' || preparedByFilter !== 'all' || sortBy !== 'newest');

  const toggle = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const setL = (key: string, val: boolean) => setActionLoading(p => ({ ...p, [key]: val }));

  const activateSchedule = async (sch: Schedule) => {
    const key = `activate-${sch._id}`; setL(key, true);
    try {
      await api.post(`/api/accounting/payment-schedules/${sch._id}/activate`, undefined, { skipErrorToast: true });
      showToast('Schedule activated — first milestone is now Due');
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'activate this schedule');
      showToast(msg, 'err');
    }
    finally { setL(key, false); }
  };

  const activateMilestone = async (sch: Schedule, ms: Milestone) => {
    const key = `act-ms-${sch._id}-${ms._id}`; setL(key, true);
    try {
      await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/activate`, undefined, { skipErrorToast: true });
      showToast(`"${ms.name}" is now Due`);
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'activate this milestone');
      showToast(msg, 'err');
    }
    finally { setL(key, false); }
  };

  const generateInvoice = async (sch: Schedule, ms: Milestone, editedHtml?: string) => {
    const key = `gen-inv-${sch._id}-${ms._id}`; setL(key, true);
    try {
      const res = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/generate-invoice`, undefined, { skipErrorToast: true });
      const invReqId = res.data?.data?.invoiceRequestId || res.data?.invoiceRequestId;
      const invNo = res.data?.data?.invoiceNumber || '';
      const alreadyExists = res.data?.data?.alreadyExists || res.data?.alreadyExists;
      const requiresApproval = res.data?.requiresApproval || res.data?.data?.requiresApproval;

      if (editedHtml && invReqId) {
        await api.put(`/api/invoice-requests/${invReqId}`, { currentHtmlBody: editedHtml }, { skipErrorToast: true });
      }

      if (requiresApproval) {
        showToast(res.data?.message || 'Tax invoice request submitted for Superadmin approval. Notification sent to Superadmin!');
      } else if (alreadyExists) {
        showToast(`${isRunningBillSchedule(sch) ? 'Running Bill' : 'Tax Invoice'} ${invNo || ''} already exists for this milestone.`);
      } else {
        showToast(`${isRunningBillSchedule(sch) ? 'Running Bill' : 'Tax Invoice'} ${invNo || ''} generated successfully!`);
      }
      setDrawerMeta(null);
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'generate this invoice');
      showToast(msg, 'err');
    }
    finally { setL(key, false); }
  };

  const approveMilestoneInvoice = async (sch: Schedule, ms: Milestone) => {
    const key = `appr-inv-${sch._id}-${ms._id}`; setL(key, true);
    try {
      let res;
      if (ms.status === 'invoice_pending_approval') {
        res = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/approve-invoice`, undefined, { skipErrorToast: true });
      } else {
        res = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/approve-milestone`, undefined, { skipErrorToast: true });
      }
      const invNo = res.data?.data?.invoiceNumber || res.data?.invoiceNumber || ms.invoiceNumber || '';
      showToast(`${isRunningBillSchedule(sch) ? 'Running Bill' : 'Tax Invoice'} ${invNo ? `(${invNo})` : ''} approved successfully!`);
      setDrawerMeta(null);
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'approve this tax invoice');
      showToast(msg, 'err');
    } finally {
      setL(key, false);
    }
  };

  const approveMilestone = approveMilestoneInvoice;

  // ── Final tax invoice (once every running bill is paid) ──────────────────
  const generateFinalInvoice = async (sch: Schedule) => {
    const key = `final-gen-${sch._id}`; setL(key, true);
    try {
      const res = await api.post(`/api/accounting/payment-schedules/${sch._id}/final-invoice`, undefined, { skipErrorToast: true });
      showToast(res.data?.message || 'Final tax invoice raised.');
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'raise the final tax invoice'), 'err');
    } finally { setL(key, false); }
  };

  const approveFinalInvoice = async (sch: Schedule) => {
    const key = `final-appr-${sch._id}`; setL(key, true);
    try {
      const res = await api.post(`/api/accounting/payment-schedules/${sch._id}/final-invoice/approve`, undefined, { skipErrorToast: true });
      showToast(res.data?.message || 'Final tax invoice approved.');
      setDrawerMeta(null);
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'approve the final tax invoice'), 'err');
    } finally { setL(key, false); }
  };

  const rejectFinalInvoice = async (sch: Schedule) => {
    const reason = window.prompt('What needs to change on the final tax invoice?');
    if (reason === null) return;
    const key = `final-rej-${sch._id}`; setL(key, true);
    try {
      await api.post(`/api/accounting/payment-schedules/${sch._id}/final-invoice/reject`, { reason: reason.trim() || 'Revision required' }, { skipErrorToast: true });
      showToast('Final tax invoice sent back for revision.');
      setDrawerMeta(null);
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'send the final tax invoice back'), 'err');
    } finally { setL(key, false); }
  };

  // The heading is read off the downloads already recorded on the document, so this always fetches
  // the document fresh rather than reusing a cached one — a second issue must not say "Original".
  const getFinalInvoiceHtml = async (sch: Schedule): Promise<{ html: string; copyLabel: string }> => {
    const res = await api.get(`/api/invoice-requests/${sch.finalInvoiceRequestId}`);
    const doc = res.data?.request || res.data?.data || res.data;
    const copyLabel = taxInvoiceCopyLabel(Number(doc?.downloadCount) || 0);
    const savedLogo = typeof window !== 'undefined' ? (localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo') || '') : '';
    const html = generateFullTaxInvoiceHtml({
      ...applyFinalInvoice({
        companyLogo: doc?.businessLogo || savedLogo,
        companyState,
        bankAccName: doc?.bankAccName || defaultBankDetail?.accountName || '',
        bankAccNo: doc?.bankAccNo || defaultBankDetail?.accountNumber || '',
        bankName: doc?.bankName || defaultBankDetail?.bankName || '',
        bankBranch: doc?.bankBranch || defaultBankDetail?.branch || '',
        bankIfsc: doc?.bankIfsc || defaultBankDetail?.ifsc || '',
        bankUpi: doc?.bankUpi || defaultBankDetail?.upi || '',
      } as any, doc, {
        company: companyProfile as any,
        defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
      }),
      copyLabel,
    });
    return { html, copyLabel };
  };

  /** This copy left the building — recorded on the document so the next one is labelled a Copy. */
  const recordFinalInvoiceCopy = async (sch: Schedule) => {
    if (!sch.finalInvoiceRequestId) return;
    try {
      const res = await api.post(`/api/invoice-requests/${sch.finalInvoiceRequestId}/record-download`);
      const downloadCount = Number(res.data?.downloadCount) || 0;
      const last = (res.data?.downloads || []).slice(-1)[0];
      setSchedules(prev => prev.map(s => s._id === sch._id
        ? { ...s, finalInvoiceCopies: { downloadCount, lastDownloadedAt: last?.at || null, lastDownloadedBy: last?.byName || '' } }
        : s));
    } catch {
      // The PDF is already with the client; the record is retried by the next issue.
    }
  };

  const viewFinalInvoiceDrawer = async (sch: Schedule) => {
    const key = `final-view-${sch._id}`; setL(key, true);
    try {
      const { html } = await getFinalInvoiceHtml(sch);
      setDrawerMeta({
        html,
        title: `Tax Invoice — ${sch.finalInvoiceNumber || ''}`,
        sch,
        ms: sch.milestones[sch.milestones.length - 1],
        idx: sch.milestones.length - 1,
        canGenerate: false,
        isProforma: false,
        finalInvoiceRequestId: String(sch.finalInvoiceRequestId || ''),
      });
    } catch {
      showToast('Failed to load the final tax invoice', 'err');
    } finally { setL(key, false); }
  };

  const downloadFinalInvoice = async (sch: Schedule) => {
    const key = `final-dl-${sch._id}`; setL(key, true);
    try {
      const { html, copyLabel } = await getFinalInvoiceHtml(sch);
      await downloadHtmlAsPdf(html, pdfFileName(sch.finalInvoiceNumber || 'final-tax-invoice'));
      await recordFinalInvoiceCopy(sch);
      showToast(`Final tax invoice saved as “${copyLabel}”.`);
    } catch {
      showToast('Failed to download the final tax invoice', 'err');
    } finally { setL(key, false); }
  };

  const [rejectModal, setRejectModal] = useState<{
    open: boolean;
    sch: Schedule | null;
    ms: Milestone | null;
    reason: string;
    loading: boolean;
  }>({ open: false, sch: null, ms: null, reason: '', loading: false });

  const submitRejectInvoice = async () => {
    if (!rejectModal.sch || !rejectModal.ms) return;
    if (!rejectModal.reason.trim()) {
      showToast('Please provide a reason / note for requesting revision', 'err');
      return;
    }
    setRejectModal(prev => ({ ...prev, loading: true }));
    try {
      await api.post(`/api/accounting/payment-schedules/${rejectModal.sch._id}/milestones/${rejectModal.ms._id}/reject-invoice`, {
        reason: rejectModal.reason.trim(),
      }, { skipErrorToast: true });
      showToast(`Revision requested for milestone "${rejectModal.ms.name}"`);
      setRejectModal({ open: false, sch: null, ms: null, reason: '', loading: false });
      setDrawerMeta(null);
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'reject this tax invoice'), 'err');
      setRejectModal(prev => ({ ...prev, loading: false }));
    }
  };

  const postMilestoneGL = async (sch: Schedule, ms: Milestone) => {
    const key = `post-gl-${sch._id}-${ms._id}`; setL(key, true);
    try {
      await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/post-invoice`, undefined, { skipErrorToast: true });
      showToast(`GL Journal entry posted for "${ms.name}"! (Taxable Revenue, GST & Payment now in Ledger Explorer)`);
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'post this GL entry');
      showToast(msg, 'err');
    } finally { setL(key, false); }
  };

  const updateMilestoneStatus = async (sch: Schedule, ms: Milestone, newStatus: string) => {
    const key = `status-${sch._id}-${ms._id}`; setL(key, true);
    try {
      await api.patch(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/status`, { status: newStatus }, { skipErrorToast: true });
      showToast(`"${ms.name}" status updated to ${STATUS_CFG[newStatus]?.label || newStatus}`);
      await fetchSchedules();
    } catch (e: any) {
      const msg = getApiErrorMessage(e, 'update this status');
      showToast(msg, 'err');
    }
    finally { setL(key, false); }
  };

  // ── Milestone Reminder handlers ───────────────────────────────────────────
  const handleOpenMilestoneReminder = (sch: Schedule, ms: Milestone) => {
    const dueStr = ms.dueDate ? new Date(ms.dueDate).toLocaleDateString('en-IN') : '';
    setReminderTitle(`Payment reminder: ${ms.name}${dueStr ? ` (${dueStr})` : ''}`);
    setReminderNotes('');
    setReminderPreset('on_due_date');
    setReminderCustomDate('');
    setReminderModal({ open: true, sch, ms });
  };

  const handleSaveMilestoneReminder = async () => {
    if (!reminderTitle.trim() || !reminderModal) return;
    setCreatingReminder(true);
    try {
      const now = new Date();
      let dueDate = new Date(now);
      if (reminderPreset === 'tomorrow_morning') {
        dueDate.setDate(now.getDate() + 1);
        dueDate.setHours(9, 0, 0, 0);
      } else if (reminderPreset === 'in_3_days') {
        dueDate.setDate(now.getDate() + 3);
        dueDate.setHours(10, 0, 0, 0);
      } else if (reminderPreset === 'on_due_date') {
        const msDue = reminderModal.ms.dueDate ? new Date(reminderModal.ms.dueDate) : null;
        if (msDue) {
          dueDate = new Date(msDue);
          dueDate.setHours(9, 0, 0, 0);
        } else {
          dueDate.setDate(now.getDate() + 3);
          dueDate.setHours(10, 0, 0, 0);
        }
      } else if (reminderCustomDate) {
        dueDate = new Date(reminderCustomDate);
      }

      await api.post(
        `/api/accounting/payment-schedules/${reminderModal.sch._id}/milestones/${reminderModal.ms._id}/reminders`,
        {
          title: reminderTitle.trim(),
          dueDate: dueDate.toISOString(),
          notes: reminderNotes.trim(),
        }
      );

      // Notify the schedule creator about this reminder
      try {
        await api.post(`/api/accounting/payment-schedules/${reminderModal.sch._id}/milestones/${reminderModal.ms._id}/notify-reminder`, {
          title: reminderTitle.trim(),
          notes: reminderNotes.trim(),
          dueDate: dueDate.toISOString(),
        });
      } catch { }

      showToast('Reminder created successfully!');
      setReminderModal(null);
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'create reminder'), 'err');
    } finally {
      setCreatingReminder(false);
    }
  };

  const patchInvoiceHtmlWithInteractiveScript = (rawHtml: string, coLogo?: string): string => {
    let patched = rawHtml;
    const savedLogo = typeof window !== 'undefined' ? (localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo') || '') : '';
    const logoSrc = coLogo || savedLogo;
    if (logoSrc && patched.includes('id="logoSlot"') && !patched.includes('<img')) {
      patched = patched.replace(
        /<div class="logo-slot" id="logoSlot"[^>]*>[\s\S]*?<\/div>/i,
        `<div class="logo-slot" id="logoSlot" style="cursor:default;border:0;background:none"><img src="${logoSrc}" style="max-height:100%;max-width:100%;object-fit:contain"/></div>`
      );
    }

    if (patched.includes('class="toolbar"')) {
      patched = patched.replace(
        /<div class="grp">\s*(?:<select[^>]*class="btn-add-inline-select"[^>]*>[\s\S]*?<\/select>\s*)?<button[^>]*onclick="[^"]*addRow[^"]*"[^>]*>[\s\S]*?<\/div>/i,
        `<div class="grp">
    <select id="toolbarItemSelect" class="btn-add-inline-select" onchange="window.selectCatalogItem ? window.selectCatalogItem(this) : selectCatalogItem(this)">
      <option value="">+ Add Item from Catalog...</option>
      <option value="__custom__">+ Add Custom / Blank Item</option>
    </select>
    <button onclick="window.addRow ? window.addRow() : addRow()">+ Particulars / Item</button>
    <button onclick="window.addNote ? window.addNote() : addNote()">+ Note</button>
    <button onclick="window.addTerm ? window.addTerm() : addTerm()">+ Term / Condition</button>
  </div>`
      );
    }
    const stylePatch = `
<style>
table.hsn th{background:#374151}
table.msched{width:100%;table-layout:fixed;border-collapse:collapse;box-sizing:border-box}
table.msched th,table.msched td{padding:5px 3.5px;font-size:9.5px;box-sizing:border-box;vertical-align:middle}
table.msched th{background:#374151;font-size:8.5px;letter-spacing:.2px;padding:6px 3.5px;line-height:1.25}
table.msched th.c,table.msched td.c{text-align:center}
table.msched th.n,table.msched td.n{text-align:right}
table.msched th.desccol,table.msched td.desccol{text-align:left}
table.msched td.desccol{white-space:normal;word-break:break-word;overflow-wrap:break-word}
table.msched td.n,table.msched td.c{white-space:nowrap}
tbody td:first-child{position:relative}
.rowdel{position:absolute!important;top:50%!important;left:2px!important;transform:translateY(-50%)!important;z-index:5;border:0;background:#fee2e2;color:#b91c1c;border-radius:4px;width:15px;height:15px;line-height:13px;text-align:center;padding:0;font-size:11px;font-weight:700;opacity:0;transition:.15s;box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:pointer}
tr:hover .rowdel{opacity:1!important}
.btn-add-inline{font-size:9.5px;font-weight:600;padding:2px 8px;background:var(--accent-soft,#eef2fb);color:var(--accent,#1e3a8a);border:1px solid var(--accent,#1e3a8a);border-radius:4px;cursor:pointer;line-height:1.4;margin-left:auto}
.btn-add-inline:hover{background:var(--accent,#1e3a8a);color:#fff}
.btn-add-inline-select{font-size:10px;font-weight:600;padding:2px 8px;background:#fff;color:var(--accent,#1e3a8a);border:1px solid var(--accent,#1e3a8a);border-radius:4px;cursor:pointer;line-height:1.4;outline:none;max-width:260px}
.btn-add-inline-select:hover{background:var(--accent-soft,#eef2fb)}
.btn-add-inline-sm{font-size:8.5px;font-weight:600;padding:1px 6px;background:#f3f4f6;color:#374151;border:1px solid #d8dbe2;border-radius:3px;cursor:pointer;margin-left:auto}
.btn-add-inline-sm:hover{background:var(--accent-soft,#eef2fb);color:var(--accent,#1e3a8a);border-color:var(--accent,#1e3a8a)}
@media print{ .rowdel,.btn-add-inline,.btn-add-inline-sm,.btn-add-inline-select,select.btn-add-inline-select{display:none!important} }
</style>
`;

    const scriptTag = stylePatch + `
<script>
  var $  = function(s,r){ return (r||document).querySelector(s); };
  var $$ = function(s,r){ return Array.from((r||document).querySelectorAll(s)); };
  var num = function(el){ var v=parseFloat(String(el?.textContent||'').replace(/[^0-9.\\-]/g,'')); return isNaN(v)?0:v; };
  var cur = function(){ var c=$('#cur'); return c ? (c.value||'') : (document.documentElement.dataset.cur||'₹'); };
  var gstMode = function(){ var g=$('#gstMode'); return g ? g.value : (document.documentElement.dataset.gst||'intra'); };
  var docType = function(){ var d=$('#docType'); return d ? d.value : (document.documentElement.dataset.doc||'tax'); };
  var isINR = function(){ return cur()==='₹'; };
  var fmt = function(n){ return cur()+Number(n||0).toLocaleString(isINR()?'en-IN':'en-US',{minimumFractionDigits:2,maximumFractionDigits:2}); };
  var r2 = function(n){ return Math.round((Number(n)||0)*100)/100; };

  window.addRow = function addRow(desc,sub,hsn,qty,unit,rate,disc,gst){
    desc=desc||''; sub=sub||''; hsn=hsn||'998314'; qty=qty||'1'; unit=unit||'Nos'; rate=rate||'0'; disc=disc||'0'; gst=gst||'18';
    try {
      var b = $('#itemsBody') || $('#items tbody') || $('table tbody');
      if(!b){
        var sec = document.createElement('div');
        sec.innerHTML = '<div class="sec"><h2>Particulars</h2></div><table id="items"><colgroup><col style="width:22px"/><col/><col class="hsncol" style="width:60px"/><col style="width:32px"/><col style="width:34px"/><col style="width:56px"/><col style="width:34px"/><col style="width:74px"/><col style="width:32px"/><col style="width:74px"/></colgroup><thead><tr><th class="c">#</th><th>Description of goods / services</th><th class="hsncol c">HSN/SAC</th><th class="n">Qty</th><th class="c">Unit</th><th class="n">Rate</th><th class="n">Disc%</th><th class="n">Taxable</th><th class="n">GST%</th><th class="n">Tax amt</th></tr></thead><tbody id="itemsBody"></tbody></table>';
        var hdr = $('.hdr') || $('.info');
        if(hdr && hdr.parentNode) hdr.parentNode.insertBefore(sec, hdr.nextSibling);
        else ($('.body') || document.body).appendChild(sec);
        b = $('#itemsBody');
      }
      if(b){
        b.insertAdjacentHTML('beforeend', '<tr><td class="c">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumber)window.renumber();if(window.recalc)window.recalc()">×</button></td><td><div class="it-name" contenteditable data-ph="Item / service">' + desc + '</div><div class="it-desc" contenteditable data-ph="Details (optional)">' + sub + '</div></td><td class="hsncol c" contenteditable data-ph="—">' + hsn + '</td><td class="n" contenteditable>' + qty + '</td><td class="c" contenteditable data-ph="—">' + unit + '</td><td class="n" contenteditable>' + rate + '</td><td class="n" contenteditable>' + disc + '</td><td class="n calc" data-taxable>—</td><td class="n" contenteditable>' + gst + '</td><td class="n calc" data-tax>—</td></tr>');
        if(window.renumber) window.renumber();
        if(window.recalc) window.recalc();
      }
    } catch(e) { console.error('addRow error', e); }
  };

  window.renumber = function renumber(){
    try {
      var rows = $$('#itemsBody tr').length ? $$('#itemsBody tr') : $$('#items tbody tr');
      rows.forEach(function(tr,i){
        if(tr.cells && tr.cells[0]) {
          tr.cells[0].innerHTML = (i+1)+'<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumber)window.renumber();if(window.recalc)window.recalc()">×</button>';
        }
      });
    } catch(e){}
  };

  window.addMilestone = function addMilestone(name,pct,due){
    name=name||''; pct=pct||'0'; due=due||'';
    try {
      var b = $('#schedBody') || $('.msched tbody');
      if(!b){
        var sec = document.createElement('div');
        sec.id = 'schedOptSection';
        sec.setAttribute('data-opt', 'sched');
        sec.innerHTML = '<div class="sec" style="margin-top:14px;display:flex;align-items:center;justify-content:space-between"><h2>Payment Schedule &amp; Tax Division (Milestone Based)</h2></div><div class="table-wrap" style="width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;margin:10px 0"><table class="msched"><colgroup><col style="width:24px"/><col/><col style="width:48px"/><col style="width:78px"/><col style="width:66px"/><col style="width:66px"/><col style="width:66px"/><col style="width:82px"/><col style="width:72px"/></colgroup><thead><tr><th class="c">#</th><th class="desccol" style="text-align:left">Milestone / Stage</th><th class="c">% Total</th><th class="n">Taxable (INR)</th><th class="n">CGST</th><th class="n">SGST</th><th class="n">Total Tax</th><th class="n" style="line-height:1.2">Amount<br><span style="font-size:7.5px;opacity:0.9">(Incl. GST)</span></th><th class="c">Due / Trigger</th></tr></thead><tbody id="schedBody"></tbody><tfoot><tr style="background:#f3f4f6;font-weight:700"><td colspan="2" class="n">Schedule Total</td><td class="n" id="msTotalPct">0%</td><td class="n" id="msTotalTaxable">₹0.00</td><td class="n" id="msTotalCgst">₹0.00 <small style="color:#6b7280">(9%)</small></td><td class="n" id="msTotalSgst">₹0.00 <small style="color:#6b7280">(9%)</small></td><td class="n" id="msTotalTax">₹0.00</td><td class="n" id="msTotalGrand" style="color:#1e3a8a">₹0.00</td><td></td></tr></tfoot></table></div>';
        var totWrap = $('.tot-wrap') || $('#totals')?.closest('.tot-wrap');
        if(totWrap && totWrap.parentNode) totWrap.parentNode.insertBefore(sec, totWrap.nextSibling);
        else ($('.body') || document.body).appendChild(sec);
        b = $('#schedBody');
      }
      if(b){
        b.insertAdjacentHTML('beforeend', '<tr><td class="c">0<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumberSched)window.renumberSched();if(window.recalc)window.recalc()">×</button></td><td class="desccol" style="text-align:left" contenteditable data-ph="Stage name">' + (name || 'New Stage') + '</td><td class="c" contenteditable>' + (pct || '0') + '%</td><td class="n calc" data-mstaxable>—</td><td class="n calc" data-mscgst>—</td><td class="n calc" data-mssgst>—</td><td class="n calc" data-mstax>—</td><td class="n calc" data-msamt>—</td><td class="c" contenteditable data-ph="On signing / T+30">' + (due || '—') + '</td></tr>');
        if(window.renumberSched) window.renumberSched();
        if(window.recalc) window.recalc();
      }
    } catch(e){ console.error('addMilestone error', e); }
  };

  window.renumberSched = function renumberSched(){
    try {
      var rows = $$('#schedBody tr').length ? $$('#schedBody tr') : $$('.msched tbody tr');
      rows.forEach(function(tr,i){
        if(tr.cells && tr.cells[0]){
          tr.cells[0].innerHTML=(i+1)+'<button class="rowdel" title="Delete row" onclick="this.parentElement.parentElement.remove();if(window.renumberSched)window.renumberSched();if(window.recalc)window.recalc()">×</button>';
        }
      });
    } catch(e){}
  };

  window.addNote = function addNote(txt){
    txt = txt || 'New note...';
    try {
      var notes = $('#notes');
      if(!notes){
        var leftcol = $('.leftcol');
        if(!leftcol){
          var totWrap = $('.tot-wrap') || $('#totals')?.closest('.tot-wrap');
          if(totWrap){
            leftcol = document.createElement('div');
            leftcol.className = 'leftcol';
            totWrap.insertBefore(leftcol, totWrap.firstChild);
          }
        }
        if(leftcol){
          var box = document.createElement('div');
          box.className = 'words';
          box.innerHTML = '<div class="lbl">Notes</div><ul class="list" id="notes" contenteditable></ul>';
          leftcol.appendChild(box);
          notes = $('#notes');
        }
      }
      if(notes){
        var li = document.createElement('li');
        li.contentEditable = 'true';
        li.textContent = txt;
        notes.appendChild(li);
        li.focus();
      }
    } catch(e){ console.error('addNote error', e); }
  };

  window.addTerm = function addTerm(txt){
    txt = txt || 'Payment terms, delivery timeline, warranty...';
    try {
      var terms = $('#terms');
      if(!terms){
        var container = $('.body') || document.body;
        var box = document.createElement('div');
        box.innerHTML = '<div class="sec" style="margin-top:14px"><h2>Terms &amp; Conditions</h2></div><ul class="list" id="terms" contenteditable></ul>';
        var cols = $('.cols');
        if(cols && cols.parentNode) cols.parentNode.insertBefore(box, cols);
        else container.appendChild(box);
        terms = $('#terms');
      }
      if(terms){
        var li = document.createElement('li');
        li.contentEditable = 'true';
        li.textContent = txt;
        terms.appendChild(li);
        li.focus();
      }
    } catch(e){ console.error('addTerm error', e); }
  };

  window.loadLogo = function loadLogo(input){
    if(input && input.files && input.files[0]){
      var r = new FileReader();
      r.onload = function(e){
        var data = e.target.result;
        var slot = document.getElementById('logoSlot');
        if(slot){
          slot.innerHTML = '<img src="' + data + '" style="max-height:100%;max-width:100%;object-fit:contain"/>';
          slot.classList.add('has-logo');
        }
        try{ localStorage.setItem('dt_company_logo', data); }catch(err){}
      };
      r.readAsDataURL(input.files[0]);
    }
  };

  window.loadImg = function loadImg(input, target){
    if(input && input.files && input.files[0]){
      var r = new FileReader();
      r.onload = function(e){
        var data = e.target.result;
        if(target === 'logo' || !target){
          var slot = document.getElementById('logoSlot');
          if(slot){
            slot.innerHTML = '<img src="' + data + '" style="max-height:100%;max-width:100%;object-fit:contain"/>';
            slot.classList.add('has-logo');
          }
          try{ localStorage.setItem('dt_company_logo', data); }catch(err){}
        } else if(target === 'sign'){
          var slot = document.getElementById('signSlot');
          if(slot){
            slot.innerHTML = '<img src="' + data + '" style="max-height:100%;max-width:100%;object-fit:contain"/>';
            slot.classList.add('has-logo');
          }
          try{ localStorage.setItem('dt_sign_img', data); }catch(err){}
        }
      };
      r.readAsDataURL(input.files[0]);
    }
  };

  window.initLogo = function initLogo(){
    try {
      var saved = localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo');
      var slot = document.getElementById('logoSlot');
      if(slot && saved && !slot.querySelector('img')){
        slot.innerHTML = '<img src="' + saved + '" style="max-height:100%;max-width:100%;object-fit:contain"/>';
        slot.classList.add('has-logo');
      }
    } catch(e){}
  };

  window.saveProfile = function saveProfile(){
    try {
      var prof = { coName: $('.co-name')?.textContent, coTag: $('.co-tag')?.textContent, coAddr: $('.co-meta div')?.textContent };
      localStorage.setItem('dt_letterhead_profile', JSON.stringify(prof));
      alert('Letterhead profile saved!');
    } catch(e){}
  };

  window.clearProfile = function clearProfile(){
    try { localStorage.removeItem('dt_letterhead_profile'); localStorage.removeItem('dt_company_logo'); alert('Reset complete!'); location.reload(); } catch(e){}
  };

  window.printDoc = function printDoc(){ window.print(); };
  window.exportFile = function exportFile(){
    // The drawer around this page turns it into a PDF on the server and saves it.
    var name = (($('#dno')?.textContent || 'Invoice').trim().replace(/[^\w\-]+/g, '-') || 'Invoice');
    if (window.parent && window.parent !== window) window.parent.postMessage({ type: 'zynx-download-pdf', name: name }, '*');
    else window.print();
  };

  window.selectCatalogItem = function selectCatalogItem(selectEl){
    if(!selectEl || !selectEl.value) return;
    var val = selectEl.value;
    if(val === '__custom__'){
      window.addRow('New Item / Service', '', '998314', 1, 'Nos', 0, 0, 18);
      selectEl.value = '';
      return;
    }
    var opt = selectEl.selectedOptions ? selectEl.selectedOptions[0] : null;
    var name = opt ? (opt.getAttribute('data-name') || opt.textContent) : val;
    var desc = opt ? (opt.getAttribute('data-desc') || '') : '';
    var hsn = opt ? (opt.getAttribute('data-hsn') || '998314') : '998314';
    var rate = opt ? (opt.getAttribute('data-rate') || '0') : '0';
    var unit = opt ? (opt.getAttribute('data-unit') || 'Nos') : 'Nos';
    var gst = opt ? (opt.getAttribute('data-gst') || '18') : '18';
    var disc = opt ? (opt.getAttribute('data-disc') || '0') : '0';

    if(window.CATALOG_ITEMS && Array.isArray(window.CATALOG_ITEMS)){
      var found = window.CATALOG_ITEMS.find(function(p){
        return String(p._id || p.id) === String(val) || p.name === val;
      });
      if(found){
        name = found.name || name;
        desc = found.description || desc;
        hsn = found.hsnSac || found.hsnCode || found.hsn || hsn;
        rate = found.sellingPrice !== undefined ? found.sellingPrice : (found.rate !== undefined ? found.rate : rate);
        unit = (found.unit && typeof found.unit === 'object') ? (found.unit.shortName || found.unit.name || 'Nos') : (found.unit || unit);
        if(found.taxProfileId && typeof found.taxProfileId === 'object'){
          var c = found.taxProfileId.cgstRate || 0;
          var s = found.taxProfileId.sgstRate || 0;
          var i = found.taxProfileId.igstRate || 0;
          gst = i || (c + s) || gst;
        } else if(found.taxRate !== undefined){
          gst = found.taxRate;
        } else if(found.gst !== undefined){
          gst = found.gst;
        }
      }
    }

    if(val !== '__custom__'){
      var rows = $$('#itemsBody tr').length ? $$('#itemsBody tr') : $$('#items tbody tr');
      for(var idx = 0; idx < rows.length; idx++){
        var row = rows[idx];
        var nameEl = row.querySelector('.it-name');
        var rowName = nameEl ? nameEl.textContent.trim() : (row.cells && row.cells[1] ? row.cells[1].textContent.trim() : '');
        if(rowName && rowName.toLowerCase() === String(name).trim().toLowerCase()){
          var qtyCell = row.cells && row.cells[3];
          if(qtyCell){
            var currentQty = parseFloat(String(qtyCell.textContent || '').replace(/[^0-9.]/g, '')) || 1;
            qtyCell.textContent = String(currentQty + 1);
            if(window.recalc) window.recalc();
            selectEl.value = '';
            return;
          }
        }
      }
    }

    window.addRow(name, desc, hsn, 1, unit, rate, disc, gst);
    selectEl.value = '';
  };

  window.updateCatalogDropdowns = function updateCatalogDropdowns(items){
    if(!items || !items.length) return;
    window.CATALOG_ITEMS = items;
    var selects = $$('.btn-add-inline-select');
    selects.forEach(function(sel){
      var curVal = sel.value;
      var isToolbar = sel.id === 'toolbarItemSelect';
      var defaultText = isToolbar ? '+ Add Item from Catalog...' : '+ Select Item to Add...';
      var html = '<option value="">' + defaultText + '</option>';
      items.forEach(function(p){
        var id = p._id || p.id || p.name;
        var name = p.name || p.description || 'Item';
        var desc = p.description || '';
        var hsn = p.hsnSac || p.hsnCode || p.hsn || '998314';
        var rate = p.sellingPrice !== undefined ? p.sellingPrice : (p.rate !== undefined ? p.rate : 0);
        var unit = (p.unit && typeof p.unit === 'object') ? (p.unit.shortName || p.unit.name || 'Nos') : (p.unit || 'Nos');
        var gst = 18;
        if(p.taxProfileId && typeof p.taxProfileId === 'object'){
          var c = p.taxProfileId.cgstRate || 0;
          var s = p.taxProfileId.sgstRate || 0;
          var i = p.taxProfileId.igstRate || 0;
          gst = i || (c + s) || 18;
        } else if(p.taxRate !== undefined){
          gst = p.taxRate;
        } else if(p.gst !== undefined){
          gst = p.gst;
        }
        var label = name + (rate > 0 ? ' (₹' + Number(rate).toLocaleString('en-IN') + ')' : '');
        html += '<option value="' + String(id).replace(/"/g, '&quot;') + '"' +
          ' data-name="' + String(name).replace(/"/g, '&quot;') + '"' +
          ' data-desc="' + String(desc).replace(/"/g, '&quot;') + '"' +
          ' data-hsn="' + String(hsn).replace(/"/g, '&quot;') + '"' +
          ' data-rate="' + rate + '"' +
          ' data-unit="' + String(unit).replace(/"/g, '&quot;') + '"' +
          ' data-gst="' + gst + '"' +
          ' data-disc="0">' + String(label).replace(/"/g, '&quot;') + '</option>';
      });
      html += '<option value="__custom__">+ Add Custom / Blank Item</option>';
      sel.innerHTML = html;
      sel.value = curVal;
    });
  };

  if(typeof fetch === 'function'){
    fetch('/api/v1/goodday/catalog/products?limit=200')
      .then(function(r){ return r.json(); })
      .then(function(res){
        var items = (res && res.data && Array.isArray(res.data)) ? res.data : (Array.isArray(res) ? res : []);
        if(items.length > 0) window.updateCatalogDropdowns(items);
      })
      .catch(function(){
        try {
          if(window.parent && window.parent.__CATALOG_PRODUCTS__ && window.parent.__CATALOG_PRODUCTS__.length){
            window.updateCatalogDropdowns(window.parent.__CATALOG_PRODUCTS__);
          }
        } catch(e){}
      });
  var loadLogo = window.loadLogo;
  var loadImg = window.loadImg;
  var initLogo = window.initLogo;
  var addRow = window.addRow;
  var selectCatalogItem = window.selectCatalogItem;
  var updateCatalogDropdowns = window.updateCatalogDropdowns;
  var renumber = window.renumber;
  var addMilestone = window.addMilestone;
  var renumberSched = window.renumberSched;
  var addNote = window.addNote;
  var addTerm = window.addTerm;
</script>
`;

    if (patched.includes('</head>')) {
      return patched.replace('</head>', scriptTag + '</head>');
    }
    if (patched.includes('</body>')) {
      return patched.replace('</body>', scriptTag + '</body>');
    }
    return patched + scriptTag;
  };

  const getDynamicInvoiceHtml = async (sch: Schedule, ms: Milestone, idx: number): Promise<string> => {
    let docData: any = null;

    // 1. First prioritize fetching the linked Proforma Invoice (which contains live edits, discounts, GST changes)
    const proformaIdToFetch = ms.proformaRequestId || (sch as any).baseProformaId || (sch as any).proformaId;
    if (proformaIdToFetch) {
      try {
        const res = await api.get(`/api/invoice-requests/${proformaIdToFetch}`);
        const pDoc = res.data?.request || res.data?.data || res.data;
        if (pDoc) docData = pDoc;
      } catch { }
    }

    // 2. If not found by ID, query active proforma by quotation reference
    if (!docData && (sch.quotationNumber || sch.quotationId)) {
      try {
        const qFilter = sch.quotationNumber ? `sourceQuotationNumber=${sch.quotationNumber}` : `sourceQuotationId=${sch.quotationId}`;
        const res = await api.get(`/api/invoice-requests?requestType=proforma&${qFilter}`);
        const items = res.data?.requests || res.data?.items || [];
        if (items.length > 0) docData = items[0];
      } catch { }
    }

    // 3. If an explicit invoice request already exists for this milestone (a running bill is always
    // rebuilt from its stage figures, never from saved HTML of the whole proforma)
    if (ms.invoiceRequestId && !isRunningBillSchedule(sch)) {
      try {
        const res = await api.get(`/api/invoice-requests/${ms.invoiceRequestId}`);
        const invReq = res.data?.request || res.data?.data || res.data;
        if (invReq) {
          if (!docData) docData = invReq;
          // If invoice request has valid HTML and proforma wasn't specifically revised, use it
          if (!docData?.items?.length && (invReq.currentHtmlBody || invReq.invoiceHtmlBody)) {
            let existingHtml = invReq.currentHtmlBody || invReq.invoiceHtmlBody;
            if (/full\s*(?:time\s*)?payment/i.test(existingHtml)) {
              existingHtml = existingHtml.replace(/Full Payment \(\s*100%\s*\)/gi, ms.name || 'Stage 1: Advance / Mobilization')
                .replace(/Full Payment/gi, ms.name || 'Stage 1: Advance / Mobilization');
            }
            const savedLogo = typeof window !== 'undefined' ? (localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo') || '') : '';
            const coLogo = invReq.businessLogo || invReq.companyLogo || invReq.template_data?.businessLogo || invReq.template_data?.companyLogo || savedLogo;
            return patchInvoiceHtmlWithInteractiveScript(existingHtml, coLogo);
          }
        }
      } catch { }
    }

    // 4. Fallback to Quotation if still no docData
    if (!docData && sch.quotationId) {
      try {
        const res = await api.get(`/api/invoice-requests/${sch.quotationId}`);
        docData = res.data?.request || res.data?.data || res.data;
      } catch { }
    }

    if (!docData && sch.quotationNumber) {
      try {
        const res = await api.get(`/api/invoice-requests?number=${sch.quotationNumber}`);
        const items = res.data?.requests || res.data?.items || [];
        if (items.length > 0) docData = items[0];
      } catch { }
    }

    const runningBill: RunningBillPreview | null = isRunningBillSchedule(sch)
      ? ((await api.get(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/running-bill`, { skipErrorToast: true })).data?.data ?? null)
      : null;

    const td = docData?.template_data || {};
    const label = milestoneInvoiceLabel(ms.invoiceNumber || (sch.quotationNumber ? sch.quotationNumber.replace('QTN', 'INV') : 'DT/INV/2026-27/001'), idx);

    const savedLogo = typeof window !== 'undefined' ? (localStorage.getItem('dt_company_logo') || localStorage.getItem('dt_letterhead_logo') || '') : '';
    const coLogo = docData?.businessLogo || docData?.companyLogo || td.businessLogo || td.companyLogo || savedLogo;

    const msDueDateStr = ms.dueDate ? new Date(ms.dueDate).toLocaleDateString('en-IN') : 'On Signing';

    const fullContractTaxable = Number(
      (sch as any).subtotal ||
      (sch.totalAmount && (sch as any).taxAmount ? sch.totalAmount - (sch as any).taxAmount : 0) ||
      (docData?.template_data?.contractTaxable) ||
      (docData?.template_data?.subtotal) ||
      (docData?.requestType !== 'proforma' ? docData?.subtotal : 0) ||
      0
    );
    const fullContractGrand = Number(
      sch.totalAmount ||
      (docData?.template_data?.contractGrand) ||
      (docData?.template_data?.grandTotal) ||
      (docData?.requestType !== 'proforma' ? (docData?.total || docData?.grandTotal) : 0) ||
      0
    );
    const fullContractTax = Number(
      (sch as any).taxTotal ||
      (sch as any).taxAmount ||
      (fullContractGrand && fullContractTaxable ? fullContractGrand - fullContractTaxable : 0) ||
      (docData?.template_data?.contractTax) ||
      (docData?.requestType !== 'proforma' ? docData?.taxTotal : 0) ||
      0
    );

    const scale = Number(ms.percentage || 100) / 100;
    const isStageDoc = (docData?.milestoneIndex !== null && docData?.milestoneIndex !== undefined && docData?.requestType === 'proforma') ||
      (docData?.items && docData.items.length > 0 && docData.items.some((it: any) => it.discountPercent > 0 || it.discount > 0));
    const stageItems = (docData?.items && docData.items.length > 0)
      ? docData.items.map((it: any) => {
        const qty = Number(it.quantity ?? it.qty ?? 1);
        const unitPrice = Number(it.unitPrice ?? it.rate ?? 0);
        const disc = Number(it.discountPercent ?? it.discount ?? it.disc ?? 0);
        // Keep the per-line note visible: it only renders when carried inside subDescription.
        const baseSubDesc = printedLineNote(it);
        if (isStageDoc) {
          return {
            description: it.name || it.description || 'Deliverable / service',
            subDescription: baseSubDesc,
            hsn: it.hsn || it.hsnSac || '998314',
            qty,
            unit: it.unit || 'Nos',
            rate: unitPrice,
            disc,
            gst: Number(it.gstRate ?? it.gst ?? it.taxRate ?? ms.gstRate ?? 18),
          };
        }
        const lineTaxable = lineGross(qty, unitPrice, it.unit) * (1 - disc / 100);
        const stageTaxable = Math.round(lineTaxable * scale * 100) / 100;
        return {
          description: it.name || it.description || 'Deliverable / service',
          subDescription: baseSubDesc ? `${baseSubDesc} · (${ms.percentage}% milestone share)` : `Milestone share (${ms.percentage}%)`,
          hsn: it.hsn || it.hsnSac || '998314',
          qty: 1,
          unit: it.unit || 'Nos',
          rate: stageTaxable,
          disc: 0,
          gst: Number(it.gstRate ?? it.gst ?? it.taxRate ?? ms.gstRate ?? 18),
        };
      })
      : [{
        description: ms.name || `Milestone ${ms.milestoneNumber}`,
        subDescription: `Payment phase ${phaseLabel(idx)} (${ms.percentage}% of total contract value) · Quotation Ref: ${sch.quotationNumber || '—'} · Due: ${msDueDateStr}`,
        hsn: '998314',
        qty: 1,
        unit: 'Service',
        rate: ms.milestoneTaxableAmount ?? ms.netAmount ?? (ms.amountDue / (1 + ((ms.gstRate || 18) / 100))),
        gst: ms.gstRate || ((ms.cgstRate || 0) + (ms.sgstRate || 0) + (ms.igstRate || 0)) || 18,
      }];

    const invoiceData: Parameters<typeof generateFullTaxInvoiceHtml>[0] = {
      documentNumber: ms.invoiceNumber || label,
      customerName: docData?.clientName || sch.clientName || td.clientName || '',
      customerAddress: docData?.clientAddress || td.clientAddr || '',
      customerGstin: docData?.clientGstin || td.clientGstin || '',
      customerState: docData?.clientState || td.clientState || '',
      customerContact: docData?.clientPhone || td.attnPhone || '',
      customerEmail: docData?.clientEmail || td.attnEmail || '',
      dueDate: msDueDateStr,
      contractTaxable: fullContractTaxable > 0 ? fullContractTaxable : undefined,
      contractGrand: fullContractGrand > 0 ? fullContractGrand : undefined,
      contractTax: fullContractTax > 0 ? fullContractTax : undefined,
      stagePercent: Number(ms.percentage || 100),
      milestoneIndex: idx,
      stageAmountsAreFinal: true,
      items: stageItems,
      notes: [
        `Quotation Reference: ${sch.quotationNumber || '—'}`,
        `Payment Milestone ${ms.milestoneNumber} of ${sch.milestones.length}: ${ms.name} (${ms.percentage}% of total contract value)`,
        `Milestone Due Date: ${msDueDateStr}`,
      ],
      milestoneName: ms.name || `Stage ${idx + 1}: Advance / Mobilization`,
      milestonePhaseLabel: ms.name || `Stage ${idx + 1}`,
      paymentType: 'milestone_based',
      milestones: ((sch.milestones && sch.milestones.length > 0)
        ? sch.milestones.map(m => ({
          name: m.name,
          percent: m.percentage,
          due: m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : `Phase ${m.milestoneNumber}`,
          invoiceNumber: m.invoiceNumber,
          proformaNumber: m.proformaNumber,
          paidAt: lastPaymentIso(m) || undefined,
          netAmount: m.milestoneTaxableAmount,
          taxAmount: m.milestoneGST,
          amountDue: m.milestoneTotal,
        }))
        : [{
          name: ms.name || 'Stage 1: Advance / Mobilization',
          percent: ms.percentage || 100,
          due: msDueDateStr,
          invoiceNumber: ms.invoiceNumber,
          proformaNumber: ms.proformaNumber,
        }]) as any[],
      // Bank details: document's own saved values, else the organization's default Bank Detail master-data record
      bankAccName: td.bankAccName || docData?.bankAccName || defaultBankDetail?.accountName || '',
      bankAccNo: td.bankAccNo || docData?.bankAccNo || defaultBankDetail?.accountNumber || '',
      bankName: td.bankName || docData?.bankName || defaultBankDetail?.bankName || '',
      bankBranch: td.bankBranch || docData?.bankBranch || defaultBankDetail?.branch || '',
      bankIfsc: td.bankIfsc || docData?.bankIfsc || defaultBankDetail?.ifsc || '',
      bankUpi: td.bankUpi || docData?.bankUpi || defaultBankDetail?.upi || '',
      signatory: td.signatory || docData?.signatory || '',
      signatoryRole: td.signatoryRole || docData?.signatoryRole || '',
      companyState,
      companyLogo: coLogo,
    };

    return generateFullTaxInvoiceHtml(
      runningBill
        ? applyRunningBill(invoiceData, runningBill, {
          quotationNumber: sch.quotationNumber,
          proformaNumber: (sch.proformaNumber || ms.proformaNumber || docData?.number || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim(),
          company: companyProfile as any,
          defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
        })
        : invoiceData
    );
  };

  const handleSaveDrawerHtml = async (editedHtml: string) => {
    if (!drawerMeta || drawerMeta.ms.status === 'paid') return;
    const { sch, ms, isProforma } = drawerMeta;
    try {
      if (isProforma) {
        let profReqId = ms.proformaRequestId;
        if (!profReqId) {
          const genRes = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/generate-proforma`, undefined, { skipErrorToast: true });
          profReqId = genRes.data?.data?.proformaRequestId || genRes.data?.proformaRequestId || ms.proformaRequestId;
        }

        if (profReqId) {
          await api.post(`/api/invoice-requests/${profReqId}/revision`, { htmlBody: editedHtml, note: 'Saved template edits from milestone proforma drawer' }, { skipErrorToast: true });
          await api.put(`/api/invoice-requests/${profReqId}`, {
            currentHtmlBody: editedHtml,
            proformaHtmlBody: editedHtml,
          }, { skipErrorToast: true });
        }
        setDrawerMeta(prev => prev ? { ...prev, html: editedHtml, ms: { ...prev.ms, proformaRequestId: profReqId || prev.ms.proformaRequestId } } : null);
        showToast('Proforma template edits saved to database successfully!');
      } else {
        let invReqId = ms.invoiceRequestId;
        if (!invReqId) {
          const genRes = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/generate-invoice`, undefined, { skipErrorToast: true });
          invReqId = genRes.data?.data?.invoiceRequestId || genRes.data?.invoiceRequestId || ms.invoiceRequestId;
        }

        if (invReqId) {
          await api.post(`/api/invoice-requests/${invReqId}/revision`, { htmlBody: editedHtml, note: 'Saved template edits from milestone drawer' }, { skipErrorToast: true });
          await api.put(`/api/invoice-requests/${invReqId}`, {
            currentHtmlBody: editedHtml,
            invoiceHtmlBody: editedHtml,
          }, { skipErrorToast: true });
        } else if (sch.quotationId) {
          await api.post(`/api/invoice-requests/${sch.quotationId}/revision`, { htmlBody: editedHtml, note: 'Saved template edits from milestone drawer' }, { skipErrorToast: true });
          await api.put(`/api/invoice-requests/${sch.quotationId}`, {
            currentHtmlBody: editedHtml,
            invoiceHtmlBody: editedHtml,
          }, { skipErrorToast: true });
        }
        setDrawerMeta(prev => prev ? { ...prev, html: editedHtml, ms: { ...prev.ms, invoiceRequestId: invReqId || prev.ms.invoiceRequestId } } : null);
        showToast('Invoice template edits saved to database successfully!');
      }
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'save template edits'), 'err');
    }
  };

  const downloadInvoiceDirect = async (sch: Schedule, ms: Milestone, idx: number) => {
    const key = `dl-${sch._id}-${ms._id}`; setL(key, true);
    try {
      const html = await getDynamicInvoiceHtml(sch, ms, idx);
      const label = milestoneInvoiceLabel(ms.invoiceNumber || (sch.quotationNumber ? sch.quotationNumber.replace('QTN', 'INV') : 'DT/INV/2026-27/001'), idx);
      await downloadHtmlAsPdf(html, pdfFileName(label, 'invoice'));
    } catch {
      showToast('Failed to download the invoice', 'err');
    } finally { setL(key, false); }
  };

  const viewTaxInvoiceDrawer = async (sch: Schedule, ms: Milestone, idx: number) => {
    const key = `view-ti-${sch._id}-${ms._id}`; setL(key, true);
    try {
      const html = await getDynamicInvoiceHtml(sch, ms, idx);
      const invNo = ms.invoiceNumber || milestoneInvoiceLabel('', idx);
      setDrawerMeta({
        html,
        title: `${isRunningBillSchedule(sch) ? 'Running Bill' : 'Tax Invoice'} — ${invNo} (${ms.name})`,
        sch,
        ms,
        idx,
        canGenerate: false,
        isProforma: false,
      });
    } catch {
      showToast('Failed to load Tax Invoice preview', 'err');
    } finally { setL(key, false); }
  };

  const viewProformaDrawer = async (sch: Schedule, ms?: Milestone, idx = 0) => {
    const key = `view-pi-${sch._id}`; setL(key, true);
    try {
      const piNo = (sch.proformaNumber || ms?.proformaNumber || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim();
      let html = '';
      const profReqId = sch.proformaRequestId || ms?.proformaRequestId;
      if (profReqId) {
        try {
          const res = await api.get(`/api/invoice-requests/${profReqId}`);
          const pDoc = res.data?.request || res.data?.data || res.data;
          html = pDoc?.currentHtmlBody || pDoc?.proformaHtmlBody || '';
        } catch { }
      }
      if (!html) {
        html = await getDynamicInvoiceHtml(sch, ms || sch.milestones?.[0] || ({} as any), idx);
        if (piNo) {
          html = html.replace(/TAX INVOICE/g, 'PROFORMA INVOICE');
        }
      }
      setDrawerMeta({
        html,
        title: `Proforma Invoice — ${piNo || 'PI'} (${sch.clientName || 'Client'})`,
        sch,
        ms: ms || sch.milestones?.[0] || ({} as any),
        idx,
        canGenerate: false,
        isProforma: true,
      });
    } catch {
      showToast('Failed to load Proforma Invoice preview', 'err');
    } finally { setL(key, false); }
  };

  const generateMilestoneReceipt = async (sch: Schedule, ms: Milestone, idx: number) => {
    const key = `gen-rcpt-${sch._id}-${ms._id}`;
    setL(key, true);
    try {
      const res = await api.post(`/api/accounting/payment-schedules/${sch._id}/milestones/${ms._id}/generate-receipt`, undefined, { skipErrorToast: true });
      const generatedReceiptNo = res.data?.data?.receiptNo || `RCP-${ms.invoiceNumber || `${sch.quotationNumber || 'INV'}(${idx + 1})`}`;
      showToast(`Receipt ${generatedReceiptNo} generated successfully!`);
      // Update local schedule state
      setSchedules(prev => prev.map(s => s._id === sch._id ? {
        ...s,
        milestones: s.milestones.map(m => m._id === ms._id ? {
          ...m,
          receiptGenerated: true,
          receiptNo: generatedReceiptNo,
          receiptGeneratedAt: new Date().toISOString(),
        } : m),
      } : s));
      // Trigger download receipt
      handleDownloadReceipt({
        schedule: sch,
        milestone: { ...ms, receiptGenerated: true, receiptNo: generatedReceiptNo },
        receiptNo: generatedReceiptNo,
        journalNo: generatedReceiptNo,
        journalDate: new Date().toISOString(),
        customerName: sch.clientName,
        customerCode: sch.clientCode ?? sch.clientId ?? '',
        narration: `Customer Receipt — ${ms.name} (${ms.percentage}% Phase) for ${sch.clientName || sch.quotationNumber || 'Client'}`,
        totalDebit: ms.amountReceived || ms.amountInvoiced || ms.amountDue || ms.fixedAmount || 0,
        paymentMode: (ms as any).paymentMode || 'bank_transfer',
        referenceNo: (ms as any).referenceNo || '',
      });
      await fetchSchedules();
    } catch (e: any) {
      showToast(getApiErrorMessage(e, 'generate this receipt'), 'err');
    } finally {
      setL(key, false);
    }
  };

  const downloadMilestoneReceipt = (sch: Schedule, ms: Milestone, idx: number) => {
    const rcpNo = ms.receiptNo || `RCP-${ms.invoiceNumber || `${sch.quotationNumber || 'INV'}(${idx + 1})`}`;
    handleDownloadReceipt({
      schedule: sch,
      milestone: ms,
      receiptNo: rcpNo,
      journalNo: rcpNo,
      journalDate: ms.receiptGeneratedAt || (ms as any).paidAt || sch.createdAt,
      customerName: sch.clientName,
      customerCode: sch.clientCode ?? sch.clientId ?? '',
      narration: `Customer Receipt — ${ms.name} (${ms.percentage}% Phase) for ${sch.clientName || sch.quotationNumber || 'Client'}`,
      totalDebit: ms.amountReceived || ms.amountInvoiced || ms.amountDue || ms.fixedAmount || 0,
      paymentMode: (ms as any).paymentMode || 'bank_transfer',
      referenceNo: (ms as any).referenceNo || '',
    });
  };

  const kpis = useMemo(() => {
    const list = Array.isArray(schedules) ? schedules : [];
    const totalValue = list.reduce((s, x) => s + (x?.totalAmount || 0), 0);
    const totalInvoiced = list.reduce((s, x) => s + (x?.totalInvoiced || 0), 0);
    const totalReceived = list.reduce((s, x) => s + (x?.totalReceived || 0), 0);
    const totalOutstanding = list.reduce((s, x) => s + (x?.totalOutstanding || 0), 0);
    const allMs = list.flatMap(x => x?.milestones || []);
    return {
      totalValue, totalInvoiced, totalReceived, totalOutstanding,
      due: allMs.filter(m => m?.status === 'due').length,
      overdue: allMs.filter(m => m?.status === 'overdue').length,
      paid: allMs.filter(m => m?.status === 'paid').length,
    };
  }, [schedules]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <AccountingSubNav />
        <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-indigo-500" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <AccountingSubNav />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${toast.type === 'ok' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.type === 'ok' ? <CheckCheck className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {drawerMeta && (
        <InvoiceDrawer
          html={drawerMeta.html}
          title={drawerMeta.title}
          invoiceRequestId={drawerMeta.finalInvoiceRequestId ?? (drawerMeta.isProforma ? drawerMeta.ms.proformaRequestId : drawerMeta.ms.invoiceRequestId)}
          isPaid={drawerMeta.ms.status === 'paid'}
          canGenerate={drawerMeta.canGenerate && drawerMeta.ms.status !== 'paid'}
          generateBtnText={
            drawerMeta.isProforma
              ? undefined
              : drawerMeta.ms.status === 'invoice_pending_approval' && isSuperAdminOrOwner
                ? 'Approve & Generate Invoice'
                : !isSuperAdminOrOwner
                  ? 'Request Tax Invoice (Submit for Approval)'
                  : 'Generate & Save Invoice'
          }
          onClose={() => setDrawerMeta(null)}
          onSave={drawerMeta.ms.status !== 'paid' ? handleSaveDrawerHtml : undefined}
          onGenerate={
            drawerMeta.ms.status !== 'paid' && !drawerMeta.isProforma
              ? (editedHtml) => {
                if (drawerMeta.ms.status === 'invoice_pending_approval' && isSuperAdminOrOwner) {
                  return approveMilestoneInvoice(drawerMeta.sch, drawerMeta.ms);
                }
                return generateInvoice(drawerMeta.sch, drawerMeta.ms, editedHtml);
              }
              : undefined
          }
          onDownloaded={drawerMeta.finalInvoiceRequestId ? () => recordFinalInvoiceCopy(drawerMeta.sch) : undefined}
        />
      )}

      {showCreate && (
        <CreateScheduleModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchSchedules(); showToast('Payment schedule created!'); }}
        />
      )}

      <div className="max-w-7xl mx-auto py-8 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Payment Schedule</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Activate stages · Generate phase invoices · Live edit & track payments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchSchedules} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
              <Plus className="w-3.5 h-3.5" /> New Schedule
            </button>
          </div>
        </div>

        {/* Upcoming / Overdue Payment Notification Banner */}
        {upcomingMilestones.length > 0 && !dismissedBanner && (
          <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-xl p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 flex-shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-xs text-amber-900 dark:text-amber-200 uppercase tracking-wide flex items-center gap-2">
                    <span>Upcoming & Overdue Payment Schedule</span>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                      {upcomingMilestones.length}
                    </span>
                  </h4>
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">
                    Click any milestone below to jump directly to its payment schedule and take action.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStageFilter(stageFilter === 'has_overdue' ? 'all' : 'has_overdue')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${stageFilter === 'has_overdue'
                      ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                      : 'bg-white dark:bg-slate-900 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900 hover:bg-rose-50'
                    }`}
                >
                  Filter Overdue Only
                </button>
                <button
                  onClick={() => setDismissedBanner(true)}
                  className="p-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 flex-shrink-0"
                  title="Dismiss banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* All upcoming and overdue milestones */}
            <div className="max-h-72 overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {upcomingMilestones.map(({ sch, ms, daysDiff }) => {
                  const isOverdue = daysDiff < 0;
                  const isDueToday = daysDiff === 0;
                  const phaseAmt = ms.fixedAmount || ms.amountDue || (sch.totalAmount * (ms.percentage / 100)) || 0;
                  return (
                    <div
                      key={`${sch._id}-${ms._id}`}
                      onClick={() => focusSchedule(sch._id)}
                      className={cn(cardClassName({ variant: 'inset', interactive: true }), 'group flex items-start justify-between gap-2 p-2.5 border-amber-200/80 dark:border-amber-800/40 hover:border-amber-400 cursor-pointer transition-all')}
                      title="Click to view and expand this contract"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate group-hover:text-indigo-600 transition-colors">
                            {ms.name}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400">
                            ({ms.percentage}%)
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-400 truncate mt-0.5">
                          {sch.clientName || 'Client'} · <span className="font-mono text-[10px] text-indigo-600 dark:text-indigo-400">{sch.quotationNumber || 'Contract'}</span>
                        </div>
                        {ms.dueDate && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Due: {new Date(ms.dueDate).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </div>

                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                        {isOverdue ? (
                          <span className="text-[10px] text-rose-700 dark:text-rose-400 font-bold bg-rose-100 dark:bg-rose-950/70 border border-rose-200 dark:border-rose-900 px-1.5 py-0.5 rounded">
                            OVERDUE {Math.abs(daysDiff)}d
                          </span>
                        ) : isDueToday ? (
                          <span className="text-[10px] text-amber-800 font-bold bg-amber-200 px-1.5 py-0.5 rounded">
                            DUE TODAY
                          </span>
                        ) : (
                          <span className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold bg-blue-50 dark:bg-blue-950 px-1.5 py-0.5 rounded">
                            In {daysDiff}d
                          </span>
                        )}
                        <span className="font-mono font-bold text-xs text-slate-800 dark:text-slate-200">
                          {formatINR(phaseAmt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Contract Value', value: formatINR(kpis.totalValue), icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-950/40' },
            { label: 'Total Invoiced', value: formatINR(kpis.totalInvoiced), icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-950/40' },
            { label: 'Total Received', value: formatINR(kpis.totalReceived), icon: Receipt, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
            { label: 'Outstanding', value: formatINR(kpis.totalOutstanding), icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/40' },
            { label: 'Due Now', value: String(kpis.due), icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-950/40' },
            { label: 'Overdue', value: String(kpis.overdue), icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/40' },
            { label: 'Phases Paid', value: String(kpis.paid), icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
          ].map(k => {
            const Icon = k.icon;
            return (
              <div key={k.label} className={cn(cardClassName(), 'p-3.5')}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 leading-tight">{k.label}</span>
                  <div className={`p-1 rounded-lg ${k.bg}`}><Icon className={`w-3 h-3 ${k.color}`} /></div>
                </div>
                <p className={`text-base font-bold ${k.color}`}>{k.value}</p>
              </div>
            );
          })}
        </div>

        {/* Filtering & Search Toolbar */}
        <div className={cn(cardClassName(), 'p-4 space-y-3')}>
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search quotation #, client, SO, invoice, milestone..."
                className="w-full pl-9 pr-8 py-2 text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={expandAll}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
                title="Expand all contracts"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
                title="Collapse all contracts"
              >
                Collapse All
              </button>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-300 hover:bg-rose-100 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
                </button>
              )}
            </div>
          </div>

          {/* Filter Dropdowns Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
            {/* Schedule Status Filter */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Contract Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="partially_complete">Partially Complete</option>
                <option value="completed">Completed</option>
                <option value="draft">Draft</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Payment Type Filter */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Payment Type</label>
              <select
                value={paymentTypeFilter}
                onChange={e => setPaymentTypeFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
              >
                <option value="all">All Types</option>
                <option value="milestone_based">Milestone Based</option>
                <option value="full_payment">Full Payment</option>
              </select>
            </div>

            {/* Stage / Payment Status Quick Filter */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Phase Stage</label>
              <select
                value={stageFilter}
                onChange={e => setStageFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
              >
                <option value="all">All Stages</option>
                <option value="has_overdue">⚠️ Has Overdue Phases</option>
                <option value="has_due">⏰ Has Due Now</option>
                <option value="has_pending">🔒 Has Pending Activation</option>
                <option value="has_invoiced">📄 Invoiced / Pending Approval</option>
                <option value="has_partially_paid">💳 Partially Paid</option>
                <option value="fully_paid">✅ Fully Paid (100%)</option>
              </select>
            </div>

            {/* Prepared By Filter */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Prepared By</label>
              <select
                value={preparedByFilter}
                onChange={e => setPreparedByFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
              >
                <option value="all">All Prepared By</option>
                {preparedByOptions.map(name => (
                  <option key={name} value={name}>Prepared by {name}</option>
                ))}
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="highest_value">Highest Value</option>
                <option value="lowest_value">Lowest Value</option>
                <option value="client_az">Client Name (A–Z)</option>
                <option value="progress_asc">Lowest Progress First</option>
              </select>
            </div>
          </div>

          {/* Results Summary Counter */}
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>
              Showing <strong>{filteredSchedules.length}</strong> of <strong>{schedules.length}</strong> payment contracts
            </span>
            {hasActiveFilters && (
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                Active filters applied
              </span>
            )}
          </div>
        </div>

        {/* Lifecycle Legend */}
        <div className={cn(cardClassName(), 'px-5 py-3')}>
          <div className="flex items-center gap-2 flex-wrap text-[10px] font-semibold text-slate-500">
            <span className="uppercase tracking-wide">Lifecycle:</span>
            {(['pending', 'due', 'invoice_generated', 'partially_paid', 'paid'] as const).map((s, i, arr) => (
              <span key={s} className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded-full ${STATUS_CFG[s]?.bg || 'bg-slate-100'} ${STATUS_CFG[s]?.text || 'text-slate-600'}`}>{STATUS_CFG[s]?.label || s}</span>
                {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
              </span>
            ))}
          </div>
        </div>

        {/* Schedules */}
        {schedules.length === 0 ? (
          <div className="text-center py-20">
            <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm font-medium">No payment schedules yet</p>
            <p className="text-slate-400 text-xs mt-1 mb-5">Create a schedule from an approved quotation with milestones</p>
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow">
              <Plus className="w-4 h-4" /> Create First Schedule
            </button>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <div className={cn(cardClassName(), 'text-center py-16 p-6')}>
            <Filter className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-700 dark:text-slate-300 text-sm font-semibold">No payment schedules match your active filters</p>
            <p className="text-slate-400 text-xs mt-1 mb-4">Try clearing or adjusting your search term and filter criteria.</p>
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSchedules.map(sch => {
              const isExpanded = !!expanded[sch._id];
              const isFullPaymentContract = sch.paymentType !== 'milestone_based' && !(sch.milestones && sch.milestones.length > 1) && (
                sch.paymentType === 'full_payment' ||
                (!sch.milestones?.length) ||
                (sch.milestones?.length === 1 && sch.milestones[0].percentage === 100 && !sch.milestones[0].name?.toLowerCase().includes('stage')) ||
                (sch as any).billingType === 'full_payment'
              );
              return (
                <div id={`sch-${sch._id}`} key={sch._id} className={cn(cardClassName(), 'overflow-hidden transition-all')}>
                  {/* Schedule Header */}
                  <div className="px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-2 rounded-xl flex-shrink-0 ${sch.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/40' : sch.status !== 'draft' ? 'bg-indigo-50 dark:bg-indigo-950/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <BarChart3 className={`w-4 h-4 ${sch.status === 'completed' ? 'text-emerald-600' : sch.status !== 'draft' ? 'text-indigo-600' : 'text-slate-400'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-slate-900 dark:text-slate-100">
                            {(() => {
                              const displayPi = sch.proformaNumber ? fmtProforma(sch.proformaNumber) : '';
                              const displayQt = sch.quotationNumber ? fmtQuotation(sch.quotationNumber) : '';
                              return (
                                displayPi ? `${displayPi} · ${displayQt || 'Contract'}` : (displayQt || `SCH-${sch._id.slice(-6).toUpperCase()}`)
                              );
                            })()}
                          </span>
                          <StatusBadge status={sch.status} />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {sch.clientName || '—'}{sch.clientCode ? ` (${sch.clientCode})` : (sch.clientId && !/^[0-9a-fA-F]{24}$/.test(String(sch.clientId)) ? ` (${sch.clientId})` : '')} · {isFullPaymentContract ? 'Full Payment (100%)' : `${sch.milestones?.length || 1} phases`} · {sch.paymentProgress}% received{schedulePreparedBy(sch) ? ` · By ${schedulePreparedBy(sch)}` : ''}{isRunningBillSchedule(sch) ? ` · ${sch.milestones.filter((m) => m.invoiceNumber && ['invoice_generated', 'approved', 'partially_paid', 'paid'].includes(m.status)).length}/${sch.milestones.length} running bills issued` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="hidden lg:flex gap-5 text-center">
                        {[
                          { label: 'Contract', value: formatINR(sch.totalAmount), color: 'text-slate-900 dark:text-slate-100' },
                          { label: 'Invoiced', value: formatINR(sch.totalInvoiced), color: 'text-blue-600' },
                          { label: 'Received', value: formatINR(sch.totalReceived), color: 'text-emerald-600' },
                          { label: 'Outstanding', value: formatINR(sch.totalOutstanding), color: 'text-amber-600' },
                        ].map(k => (
                          <div key={k.label}>
                            <p className="text-[10px] text-slate-500">{k.label}</p>
                            <p className={`text-sm font-bold ${k.color}`}>{k.value}</p>
                          </div>
                        ))}
                      </div>
                      {sch.status === 'draft' && (
                        <button
                          onClick={() => activateSchedule(sch)}
                          disabled={actionLoading[`activate-${sch._id}`]}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 shadow-sm"
                        >
                          {actionLoading[`activate-${sch._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                          Activate Schedule
                        </button>
                      )}
                      <button onClick={() => toggle(sch._id)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="px-5 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full transition-all duration-700" style={{ width: `${sch.paymentProgress}%` }} />
                      </div>
                      <span className="text-[10px] font-bold text-indigo-600 w-8 text-right">{sch.paymentProgress}%</span>
                    </div>
                  </div>

                  {/* Final tax invoice — raised once every running bill is paid */}
                  {isRunningBillSchedule(sch) && (() => {
                    const finalStatus = sch.finalInvoiceStatus || '';
                    const raised = FINAL_RAISED.includes(finalStatus);
                    const paidStages = sch.milestones.filter((m) => m.status === 'paid' && m.invoiceNumber).length;
                    const ready = allStagesPaid(sch);
                    if (!raised && finalStatus !== 'revision_needed' && paidStages === 0) return null;
                    return (
                      <div className="mx-5 mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 px-4 py-2.5">
                        <Receipt className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 dark:text-slate-100">
                            Final Tax Invoice{sch.finalInvoiceNumber ? ` · ${sch.finalInvoiceNumber}` : ''}
                            {finalStatus === 'invoice_pending_approval' && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">Awaiting approval</span>}
                            {(finalStatus === 'approved' || finalStatus === 'sent') && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">Issued</span>}
                            {finalStatus === 'revision_needed' && <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">Needs revision</span>}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {raised ? 'Consolidated invoice for the completed contract; GST was charged on the running bills.'
                              : ready ? 'Every stage is billed and paid in full. The final invoice can be raised.'
                                : `Available once every stage is billed and paid (${paidStages} of ${sch.milestones.length} paid).`}
                          </p>
                          {/* The record of every copy handed out — the first is the original, the rest are Copies. */}
                          {raised && (() => {
                            const issued = sch.finalInvoiceCopies?.downloadCount || 0;
                            const at = sch.finalInvoiceCopies?.lastDownloadedAt;
                            const by = sch.finalInvoiceCopies?.lastDownloadedBy;
                            return (
                              <p className="text-[11px] text-slate-500">
                                {issued === 0
                                  ? 'Nothing issued yet — the first download is the Original for Recipient.'
                                  : `${issued} ${issued === 1 ? 'copy' : 'copies'} issued · the next is “${taxInvoiceCopyLabel(issued)}”${at ? ` · last downloaded ${new Date(at).toLocaleDateString('en-IN')}${by ? ` by ${by}` : ''}` : ''}`}
                              </p>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {raised && (
                            <>
                              <button
                                onClick={() => viewFinalInvoiceDrawer(sch)}
                                disabled={actionLoading[`final-view-${sch._id}`]}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 cursor-pointer"
                              >
                                {actionLoading[`final-view-${sch._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                                View Final Tax Invoice
                              </button>
                              <button
                                onClick={() => downloadFinalInvoice(sch)}
                                disabled={actionLoading[`final-dl-${sch._id}`]}
                                title={`Saves as “${taxInvoiceCopyLabel(sch.finalInvoiceCopies?.downloadCount || 0)}”`}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 cursor-pointer"
                              >
                                <Download className="w-3.5 h-3.5" /> Download
                              </button>
                            </>
                          )}
                          {finalStatus === 'invoice_pending_approval' && isSuperAdminOrOwner && (
                            <>
                              <button
                                onClick={() => approveFinalInvoice(sch)}
                                disabled={actionLoading[`final-appr-${sch._id}`]}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 cursor-pointer"
                              >
                                {actionLoading[`final-appr-${sch._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                Approve
                              </button>
                              <button
                                onClick={() => rejectFinalInvoice(sch)}
                                disabled={actionLoading[`final-rej-${sch._id}`]}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-300 cursor-pointer"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Reject / Revise
                              </button>
                            </>
                          )}
                          {!raised && (
                            <button
                              onClick={() => generateFinalInvoice(sch)}
                              disabled={!ready || actionLoading[`final-gen-${sch._id}`]}
                              title={ready ? undefined : 'Every stage must be billed and paid in full first'}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                              {actionLoading[`final-gen-${sch._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                              {finalStatus === 'revision_needed' ? (isSuperAdminOrOwner ? 'Re-generate Final Tax Invoice' : 'Resubmit Final Tax Invoice') : (isSuperAdminOrOwner ? 'Generate Final Tax Invoice' : 'Request Final Tax Invoice')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Milestone Table or Full Payment Single Invoice */}
                  {isExpanded && (
                    isFullPaymentContract ? (() => {
                      const singleProformaNo = (sch.proformaNumber || sch.milestones?.[0]?.proformaNumber || (sch as any).baseProformaNumber || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim();
                      const displayPiNo = singleProformaNo ? fmtProforma(singleProformaNo) : '';

                      const fullMs: Milestone = (sch.milestones && sch.milestones.length > 0)
                        ? sch.milestones[0]
                        : {
                          _id: `${sch._id}_full`,
                          milestoneNumber: 1,
                          name: sch.milestones?.[0]?.name || 'Stage 1: Advance / Mobilization',
                          percentage: 100,
                          fixedAmount: 0,
                          amountDue: sch.totalAmount,
                          amountInvoiced: sch.totalInvoiced || 0,
                          amountReceived: sch.totalReceived || 0,
                          amountOutstanding: sch.totalOutstanding || sch.totalAmount,
                          status: sch.totalReceived >= sch.totalAmount && sch.totalAmount > 0 ? 'paid' : (sch.totalReceived > 0 ? 'partially_paid' : (sch.status === 'active' ? 'due' : 'pending')),
                          invoiceNumber: (sch.invoiceNumber || (sch as any).baseInvoiceNumber || '').replace(/\s*\([IVXLCDM]+\)\s*$/i, '').trim(),
                          invoiceRequestId: null,
                          proformaNumber: singleProformaNo,
                          dueDate: (sch as any).dueDate || null,
                          triggerType: 'full_payment',
                        } as Milestone;

                      const isInvoiceOfficial = ['invoice_generated', 'approved', 'partially_paid', 'paid'].includes(fullMs.status) || Boolean(fullMs.invoiceNumber);
                      const rawInvNo = isInvoiceOfficial
                        ? (fullMs.invoiceNumber || (fullMs.invoiceRequestId ? (sch.invoiceNumber || (sch as any).baseInvoiceNumber || '') : ''))
                        : '';
                      const displayInvNo = rawInvNo ? fmtInvoice(rawInvNo) : '';
                      // Paid in full AND invoiced. Money received before the tax invoice does not make it "paid" yet.
                      const isPaidMs = Boolean(displayInvNo) && (fullMs.status === 'paid' || (sch.totalReceived >= sch.totalAmount && sch.totalAmount > 0));

                      return (
                        <div className="border-t border-slate-100 dark:border-slate-800 p-5 bg-gradient-to-r from-slate-50/60 via-white to-blue-50/20 dark:from-slate-900/40 dark:via-slate-900 dark:to-blue-950/20">
                          <div className={cn(cardClassName(), 'flex flex-col lg:flex-row lg:items-center justify-between gap-5 p-5')}>
                            <div className="space-y-2 min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                  100% Full Payment Contract
                                </span>
                                <StatusBadge status={fullMs.status} />
                              </div>
                              <h4 className="text-base font-bold text-slate-900 dark:text-slate-100">
                                Final Tax Invoice
                              </h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                This contract is configured for one-time full billing. No milestone stages are required.
                              </p>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                <div>
                                  <span className="text-[11px] font-medium text-slate-400 block">Total Contract Value</span>
                                  <span className="text-sm font-bold font-mono text-slate-900 dark:text-slate-100">{formatINR(sch.totalAmount)}</span>
                                </div>
                                <div>
                                  <span className="text-[11px] font-medium text-slate-400 block">Proforma Invoice Ref</span>
                                  {displayPiNo ? (
                                    <button
                                      type="button"
                                      onClick={() => viewProformaDrawer(sch, fullMs, 0)}
                                      className="inline-flex items-center gap-1 font-mono text-xs font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md hover:bg-indigo-100 transition-colors cursor-pointer mt-0.5"
                                      title={`View Proforma Invoice ${displayPiNo}`}
                                    >
                                      <FileText className="w-3 h-3 flex-shrink-0" />PI: {displayPiNo}
                                    </button>
                                  ) : (
                                    <span className="text-xs font-mono text-slate-400 mt-0.5 block">—</span>
                                  )}
                                </div>
                                <div>
                                  <span className="text-[11px] font-medium text-slate-400 block">Tax Invoice Ref</span>
                                  {displayInvNo ? (
                                    <button
                                      type="button"
                                      onClick={() => viewTaxInvoiceDrawer(sch, fullMs, 0)}
                                      className="inline-flex items-center gap-1 font-mono text-xs font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md hover:bg-emerald-100 transition-colors cursor-pointer mt-0.5"
                                      title={`View Tax Invoice ${displayInvNo}`}
                                    >
                                      <Receipt className="w-3 h-3 text-emerald-600 flex-shrink-0" />TI: {displayInvNo}
                                    </button>
                                  ) : fullMs.status === 'invoice_pending_approval' ? (
                                    <button
                                      type="button"
                                      onClick={() => viewTaxInvoiceDrawer(sch, fullMs, 0)}
                                      className="inline-flex items-center gap-1 font-mono text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-md border border-dashed border-amber-300 hover:bg-amber-100 transition-colors cursor-pointer mt-0.5"
                                    >
                                      <Clock className="w-3 h-3 text-amber-600 animate-pulse flex-shrink-0" />Awaiting Approval
                                    </button>
                                  ) : (
                                    <span className="text-xs font-mono text-slate-400 mt-0.5 block">Pending Generation</span>
                                  )}
                                </div>
                                <div>
                                  <span className="text-[11px] font-medium text-slate-400 block">Received / Outstanding</span>
                                  <div className="mt-0.5 font-mono text-xs">
                                    <span className="text-emerald-600 font-bold">{formatINR(fullMs.amountReceived || sch.totalReceived || 0)}</span>
                                    <span className="text-slate-300 mx-1">/</span>
                                    <span className="text-amber-600 font-bold">{formatINR(fullMs.amountOutstanding ?? (sch.totalOutstanding || sch.totalAmount))}</span>
                                  </div>
                                  {(fullMs.payments?.length || 0) > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setHistoryMeta({ sch, ms: fullMs })}
                                      className="mt-0.5 text-[11px] font-semibold text-indigo-600 hover:underline cursor-pointer"
                                    >
                                      {fullMs.payments!.length} payment{fullMs.payments!.length > 1 ? 's' : ''} received
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Final One Invoice Generation Option */}
                            <div className="flex items-center gap-2 flex-wrap lg:flex-col lg:items-end flex-shrink-0 pt-2 lg:pt-0">
                              {fullMs._id && !String(fullMs._id).endsWith('_full') && canRecordPayment(fullMs) && (
                                <button
                                  onClick={() => setPayMeta({ sch, ms: fullMs })}
                                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap shadow-sm cursor-pointer"
                                  title="Record a payment received against this milestone"
                                >
                                  <Wallet className="w-3.5 h-3.5" /> Record Payment
                                </button>
                              )}
                              {isPaidMs ? (
                                <>
                                  <button
                                    onClick={() => viewTaxInvoiceDrawer(sch, fullMs, 0)}
                                    disabled={actionLoading[`view-ti-${sch._id}-${fullMs._id}`]}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                  >
                                    {actionLoading[`view-ti-${sch._id}-${fullMs._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                                    View Tax Invoice {displayInvNo ? `(${displayInvNo})` : ''}
                                  </button>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => downloadInvoiceDirect(sch, fullMs, 0)}
                                      disabled={actionLoading[`dl-${sch._id}-${fullMs._id}`]}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
                                    >
                                      {actionLoading[`dl-${sch._id}-${fullMs._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                      Download Invoice
                                    </button>
                                    <button
                                      onClick={() => downloadMilestoneReceipt(sch, fullMs, 0)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800 cursor-pointer shadow-sm"
                                    >
                                      <Download className="w-3.5 h-3.5 text-emerald-600" />
                                      Download Receipt
                                    </button>
                                  </div>
                                </>
                              ) : (!displayInvNo || fullMs.status === 'revision_needed') && fullMs.status !== 'invoice_pending_approval' && fullMs.status !== 'approved' && fullMs.status !== 'invoice_generated' ? (isFullyReceived(fullMs) || fullMs.status === 'revision_needed' ? (
                                <button
                                  onClick={() => generateInvoice(sch, fullMs)}
                                  disabled={actionLoading[`gen-inv-${sch._id}-${fullMs._id}`]}
                                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-md cursor-pointer transition-all hover:scale-[1.02]"
                                  title={isSuperAdminOrOwner ? 'Generate Final Tax Invoice' : 'Request Final Tax Invoice (Submits to Superadmin for approval)'}
                                >
                                  {actionLoading[`gen-inv-${sch._id}-${fullMs._id}`] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Receipt className="w-4 h-4" />
                                  )}
                                  {fullMs.status === 'revision_needed' ? (isSuperAdminOrOwner ? 'Re-generate Tax Invoice' : 'Resubmit Tax Invoice') : (isSuperAdminOrOwner ? 'Generate Tax Invoice' : 'Request Tax Invoice')}
                                </button>
                              ) : null) : (
                                <div className="flex items-center gap-2 flex-wrap lg:flex-col lg:items-end">
                                  {isSuperAdminOrOwner && fullMs.status === 'invoice_pending_approval' && (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => approveMilestoneInvoice(sch, fullMs)}
                                        disabled={actionLoading[`appr-inv-${sch._id}-${fullMs._id}`]}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                      >
                                        {actionLoading[`appr-inv-${sch._id}-${fullMs._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                        Approve Tax Invoice
                                      </button>
                                      <button
                                        onClick={() => setRejectModal({ open: true, sch, ms: fullMs, reason: '', loading: false })}
                                        className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-bold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 cursor-pointer"
                                      >
                                        <XCircle className="w-3.5 h-3.5" /> Reject / Revise
                                      </button>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => viewTaxInvoiceDrawer(sch, fullMs, 0)}
                                      disabled={actionLoading[`view-ti-${sch._id}-${fullMs._id}`]}
                                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                    >
                                      {actionLoading[`view-ti-${sch._id}-${fullMs._id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                                      View Tax Invoice {displayInvNo ? `(${displayInvNo})` : ''}
                                    </button>
                                    {displayInvNo && isFullyReceived(fullMs) && (
                                      <button
                                        onClick={() => downloadInvoiceDirect(sch, fullMs, 0)}
                                        disabled={actionLoading[`dl-${sch._id}-${fullMs._id}`]}
                                        className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 cursor-pointer"
                                      >
                                        <Download className="w-3.5 h-3.5" /> Download
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="border-t border-slate-100 dark:border-slate-800 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 dark:bg-slate-800/60">
                            <tr>
                              {['#', 'Milestone / Stage', '%', 'Amount', isRunningBillSchedule(sch) ? 'PI / Running Bill' : 'Invoice Ref', 'Received', 'Outstanding', 'Status pending', 'Actions'].map(h => (
                                <th key={h} className="px-4 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wide text-[10px] whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {((sch.milestones && sch.milestones.length > 0) ? sch.milestones : [{
                              _id: `${sch._id}_full`,
                              milestoneNumber: 1,
                              name: 'Full Payment (100%)',
                              percentage: 100,
                              fixedAmount: 0,
                              amountDue: sch.totalAmount,
                              amountInvoiced: sch.totalInvoiced || 0,
                              amountReceived: sch.totalReceived || 0,
                              amountOutstanding: sch.totalOutstanding || sch.totalAmount,
                              status: sch.totalReceived >= sch.totalAmount && sch.totalAmount > 0 ? 'paid' : (sch.totalReceived > 0 ? 'partially_paid' : (sch.status === 'active' ? 'due' : 'pending')),
                              invoiceNumber: (sch.invoiceNumber || sch.baseInvoiceNumber || '').replace(/\s*\([IVXLCDM]+\)\s*$/i, '').trim(),
                              invoiceRequestId: null,
                              proformaNumber: (sch.proformaNumber || '').replace(/\s*\([IVXLCDM]+\)\s*$/i, '').trim(),
                              dueDate: sch.dueDate || null,
                              triggerType: 'full_payment',
                            } as Milestone]).map((ms, idx) => {
                              const actKey = `act-ms-${sch._id}-${ms._id}`;
                              const dlKey = `dl-${sch._id}-${ms._id}`;
                              const isPaidMs = ms.status === 'paid';

                              const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
                              const roman = ROMANS[idx] || String(idx + 1);
                              const isMultiple = (sch.milestones && sch.milestones.length > 1);
                              const billWord = isMultiple ? 'Running Bill' : 'Tax Invoice';

                              // The single proforma invoice for ALL stages of this quotation / contract
                              const singleProformaNo = (sch.proformaNumber || ms.proformaNumber || (sch as any).baseProformaNumber || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim();
                              const displayPiNo = singleProformaNo ? fmtProforma(singleProformaNo) : '';

                              // The stage/phase-wise tax invoice (official only when generated/approved/paid)
                              const isInvoiceOfficial = ['invoice_generated', 'approved', 'partially_paid', 'paid'].includes(ms.status);
                              let rawInvNo = isInvoiceOfficial
                                ? (ms.invoiceNumber || (ms.invoiceRequestId ? (isMultiple ? `${(sch.invoiceNumber || sch.baseInvoiceNumber || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '')}(${roman})` : (sch.invoiceNumber || sch.baseInvoiceNumber || '')) : ''))
                                : '';
                              let displayInvNo = rawInvNo ? fmtInvoice(rawInvNo) : '';

                              return (
                                <tr key={ms._id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${ms.status === 'due' ? 'bg-amber-50/40 dark:bg-amber-950/10' : isPaidMs ? 'bg-emerald-50/30 dark:bg-emerald-950/10' : ''}`}>
                                  <td className="px-4 py-3 font-bold text-slate-300 dark:text-slate-600 text-center">{idx + 1}x</td>
                                  <td className="px-4 py-3 min-w-[160px]">
                                    <div className="font-semibold text-slate-800 dark:text-slate-200">{ms.name}</div>
                                    {ms.status === 'revision_needed' && (ms.rejectionReason || ms.revisionNote) && (
                                      <div className="text-[10px] text-orange-800 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded px-1.5 py-0.5 mt-1 inline-block">
                                        <span className="font-semibold">Revision Required:</span> {ms.rejectionReason || ms.revisionNote}
                                      </div>
                                    )}
                                    <MilestoneProgress status={ms.status} />
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <CountdownBadge dueDate={ms.dueDate} status={ms.status} />
                                  </td>
                                  <td className="px-4 py-3 text-center font-mono text-slate-600 dark:text-slate-400">{ms.percentage}%</td>
                                  <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <div className="font-semibold text-slate-800 dark:text-slate-200">{formatINR(ms.milestoneTotal || ms.amountDue)}</div>
                                    {/* GST breakdown — read from stored proportional allocation or compute proportionally if 0 */}
                                    {(() => {
                                      const totalVal = ms.milestoneTotal || ms.amountDue || 0;
                                      let taxableVal = Number(ms.milestoneTaxableAmount ?? ms.netAmount ?? 0);
                                      let cgstVal = Number(ms.milestoneCGST ?? ms.cgstAmount ?? 0);
                                      let sgstVal = Number(ms.milestoneSGST ?? ms.sgstAmount ?? 0);

                                      // If amounts are 0 but stage has a total amount, compute cleanly from total using 18% GST (standard in India)
                                      if ((!taxableVal || taxableVal === 0) && totalVal > 0) {
                                        taxableVal = Math.round((totalVal / 1.18) * 100) / 100;
                                        const totalGst = Math.round((totalVal - taxableVal) * 100) / 100;
                                        cgstVal = Math.round((totalGst / 2) * 100) / 100;
                                        sgstVal = totalGst - cgstVal;
                                      }

                                      return (
                                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 space-y-0.5">
                                          <div>Taxable: {formatINR(taxableVal)}</div>
                                          {ms.intraState !== false ? (
                                            <>
                                              <div>CGST{ms.cgstRate ? ` ${ms.cgstRate}%` : ' 9%'}: {formatINR(cgstVal)}</div>
                                              <div>SGST{ms.sgstRate ? ` ${ms.sgstRate}%` : ' 9%'}: {formatINR(sgstVal)}</div>
                                            </>
                                          ) : (
                                            <div>IGST{ms.igstRate ? ` ${ms.igstRate}%` : ' 18%'}: {formatINR(cgstVal + sgstVal)}</div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-4 py-3 min-w-[140px]">
                                    <div className="flex flex-col gap-1">
                                      {displayPiNo && (
                                        <button
                                          type="button"
                                          onClick={() => viewProformaDrawer(sch, ms, idx)}
                                          className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-md w-max hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors cursor-pointer text-left"
                                          title={`View Master Proforma Invoice ${displayPiNo}`}
                                        >
                                          <FileText className="w-3 h-3 flex-shrink-0" />PI: {displayPiNo}
                                        </button>
                                      )}
                                      {displayInvNo ? (
                                        <button
                                          type="button"
                                          onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                          className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md w-max hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors cursor-pointer text-left"
                                          title={`View Tax Invoice ${displayInvNo}`}
                                        >
                                          <Receipt className="w-3 h-3 text-emerald-600 flex-shrink-0" />{isMultiple ? 'RB' : 'TI'}: {displayInvNo}
                                        </button>
                                      ) : ms.status === 'invoice_pending_approval' ? (
                                        <button
                                          type="button"
                                          onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                          className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-md w-max border border-dashed border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors cursor-pointer text-left"
                                          title="Click to view Tax Invoice awaiting approval"
                                        >
                                          <Clock className="w-3 h-3 text-amber-600 flex-shrink-0 animate-pulse" />{isMultiple ? 'RB' : 'TI'}: awaiting approval
                                        </button>
                                      ) : displayPiNo ? (
                                        <span className="inline-flex items-center gap-1 font-mono text-[10px] text-slate-400 dark:text-slate-500 px-2 py-0.5 rounded-md w-max border border-dashed border-slate-200 dark:border-slate-700">
                                          {isMultiple ? 'RB' : 'TI'}: pending
                                        </span>
                                      ) : (
                                        <span className="text-slate-300 dark:text-slate-600">—</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-emerald-600 whitespace-nowrap">
                                    {formatINR(ms.amountReceived)}
                                    {(ms.payments?.length || 0) > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => setHistoryMeta({ sch, ms })}
                                        className="block ml-auto text-[10px] font-sans font-semibold text-indigo-600 hover:underline cursor-pointer"
                                      >
                                        {ms.payments!.length} payment{ms.payments!.length > 1 ? 's' : ''}
                                      </button>
                                    )}
                                    {lastPaidAt(ms) && (
                                      <span className="block text-[10px] font-sans font-normal text-slate-500">on {lastPaidAt(ms)}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-amber-600 whitespace-nowrap">{formatINR(ms.amountOutstanding)}</td>
                                  <td className="px-4 py-3">
                                    <div className="relative inline-block">
                                      <select
                                        value={ms.status}
                                        disabled={actionLoading[`status-${sch._id}-${ms._id}`]}
                                        onChange={e => updateMilestoneStatus(sch, ms, e.target.value)}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-xs"
                                        title="Click to change status"
                                      >
                                        <option value="pending">Pending</option>
                                        <option value="due">Due</option>
                                        <option value="proforma_pending_approval">Proforma Awaiting Approval</option>
                                        <option value="proforma_rejected">Proforma Rejected</option>
                                        <option value="invoice_pending_approval">Pending Approval</option>
                                        <option value="invoice_generated">Invoiced</option>
                                        <option value="approved">Approved</option>
                                        <option value="revision_needed">Revision Needed</option>
                                        <option value="partially_paid">Partially Paid</option>
                                        <option value="overdue">Overdue</option>
                                        <option value="cancelled">Cancelled</option>
                                      </select>
                                      <StatusBadge status={ms.status} />
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 min-w-[220px]">
                                    <div className="flex items-center gap-1.5 flex-wrap">

                                      {/* ── PAID STATUS CASE: View TI, Download, Download Receipt ── */}
                                      {isPaidMs ? (
                                        <>
                                          {/* View Tax Invoice button */}
                                          {(displayInvNo || ms.invoiceRequestId || ms.invoiceNumber) && (
                                            <button
                                              onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                              disabled={actionLoading[`view-ti-${sch._id}-${ms._id}`]}
                                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm"
                                              title={`View Tax Invoice ${displayInvNo || ms.invoiceNumber || ''}`}
                                            >
                                              {actionLoading[`view-ti-${sch._id}-${ms._id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
                                              View TI {displayInvNo || ms.invoiceNumber ? `(${displayInvNo || ms.invoiceNumber})` : ''}
                                            </button>
                                          )}

                                          <button
                                            onClick={() => downloadInvoiceDirect(sch, ms, idx)}
                                            disabled={actionLoading[dlKey]}
                                            title="Download paid phase invoice PDF"
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-60 whitespace-nowrap"
                                          >
                                            {actionLoading[dlKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                            Download
                                          </button>
                                          <button
                                            onClick={() => downloadMilestoneReceipt(sch, ms, idx)}
                                            title="Download paid phase customer payment receipt PDF"
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 disabled:opacity-60 whitespace-nowrap border border-emerald-200 dark:border-emerald-800 shadow-sm"
                                          >
                                            <Download className="w-3 h-3 text-emerald-600" />
                                            Download Receipt
                                          </button>
                                        </>
                                      ) : (
                                        /* ── NON-PAID STATUS CASE: Activate, Generate TI, View TI, Download, Receipt ── */
                                        <>
                                          {canRecordPayment(ms) && (
                                            <button
                                              onClick={() => setPayMeta({ sch, ms })}
                                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap shadow-sm cursor-pointer"
                                              title={`Record a payment received for ${ms.name}`}
                                            >
                                              <Wallet className="w-3 h-3" /> Record Payment
                                            </button>
                                          )}

                                          {/* Generate / Request Stage Tax Invoice button */}
                                          {(!displayInvNo || ms.status === 'revision_needed') && ms.status !== 'paid' && ms.status !== 'approved' && ms.status !== 'invoice_generated' && ms.status !== 'invoice_pending_approval' && (isFullyReceived(ms) || ms.status === 'revision_needed') && (
                                            <button
                                              onClick={() => generateInvoice(sch, ms)}
                                              disabled={actionLoading[`gen-inv-${sch._id}-${ms._id}`]}
                                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                              title={isSuperAdminOrOwner ? `Generate Stage ${billWord} for ${ms.name}` : `Request Stage ${billWord} for ${ms.name} (Submits to Superadmin for approval)`}
                                            >
                                              {actionLoading[`gen-inv-${sch._id}-${ms._id}`] ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <Receipt className="w-3 h-3" />
                                              )}
                                              {ms.status === 'revision_needed' ? (isSuperAdminOrOwner ? `Re-generate ${billWord}` : `Resubmit ${billWord}`) : (isSuperAdminOrOwner ? `Generate ${billWord}` : `Request ${billWord}`)}
                                            </button>
                                          )}

                                          {/* Superadmin Approval Button / Non-admin Pending Badge */}
                                          {(ms.status === 'invoice_pending_approval' || ms.status === 'invoice_generated') && (
                                            <>
                                              {isSuperAdminOrOwner ? (
                                                <div className="flex items-center gap-1">
                                                  <button
                                                    onClick={() => approveMilestoneInvoice(sch, ms)}
                                                    disabled={actionLoading[`appr-inv-${sch._id}-${ms._id}`]}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                                    title={`Approve Stage Tax Invoice for ${ms.name} and generate official Tax Invoice`}
                                                  >
                                                    {actionLoading[`appr-inv-${sch._id}-${ms._id}`] ? (
                                                      <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                      <CheckCircle2 className="w-3 h-3" />
                                                    )}
                                                    Approve {billWord}
                                                  </button>
                                                  <button
                                                    onClick={() => setRejectModal({ open: true, sch, ms, reason: '', loading: false })}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 whitespace-nowrap shadow-sm cursor-pointer"
                                                    title={`Reject or request revision for ${ms.name} tax invoice`}
                                                  >
                                                    <XCircle className="w-3 h-3" />
                                                    Reject / Revise
                                                  </button>
                                                  <button
                                                    onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                                    disabled={actionLoading[`view-ti-${sch._id}-${ms._id}`]}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                                    title={`View Tax Invoice for ${ms.name}`}
                                                  >
                                                    {actionLoading[`view-ti-${sch._id}-${ms._id}`] ? (
                                                      <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                      <Eye className="w-3 h-3" />
                                                    )}
                                                    View Invoice
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-1">
                                                  <span
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300 dark:border-amber-700 shadow-sm"
                                                    title="Tax Invoice request has been submitted and is awaiting Superadmin approval"
                                                  >
                                                    <Clock className="w-3 h-3 text-amber-600 animate-pulse" />
                                                    Pending Superadmin Approval
                                                  </span>
                                                  <button
                                                    onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                                    disabled={actionLoading[`view-ti-${sch._id}-${ms._id}`]}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                                    title={`View Tax Invoice awaiting approval for ${ms.name}`}
                                                  >
                                                    {actionLoading[`view-ti-${sch._id}-${ms._id}`] ? (
                                                      <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                      <Eye className="w-3 h-3" />
                                                    )}
                                                    View Invoice
                                                  </button>
                                                </div>
                                              )}
                                            </>
                                          )}

                                          {ms.status === 'pending' && sch.status === 'active' && (
                                            <button
                                              onClick={() => activateMilestone(sch, ms)}
                                              disabled={actionLoading[actKey]}
                                              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60 whitespace-nowrap"
                                            >
                                              {actionLoading[actKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                                              Activate
                                            </button>
                                          )}

                                          {/* Create Reminder button */}
                                          <button
                                            onClick={() => handleOpenMilestoneReminder(sch, ms)}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/60 whitespace-nowrap"
                                            title={`Create reminder for ${ms.name}`}
                                          >
                                            <Bell className="w-3 h-3" />
                                            Remind Me
                                          </button>



                                          {/* View Tax Invoice button — shown when TI has been generated */}
                                          {ms.status !== 'invoice_pending_approval' && ms.status !== 'invoice_generated' && (displayInvNo || (ms.invoiceNumber && isInvoiceOfficial) || ms.status === 'approved') && (
                                            <button
                                              onClick={() => viewTaxInvoiceDrawer(sch, ms, idx)}
                                              disabled={actionLoading[`view-ti-${sch._id}-${ms._id}`]}
                                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap shadow-sm cursor-pointer"
                                              title={`View Tax Invoice ${displayInvNo || ms.invoiceNumber || ''}`}
                                            >
                                              {actionLoading[`view-ti-${sch._id}-${ms._id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                                              View Invoice {displayInvNo || ms.invoiceNumber ? `(${displayInvNo || ms.invoiceNumber})` : ''}
                                            </button>
                                          )}

                                          {isFullyReceived(ms) && (
                                            <button
                                              onClick={() => downloadInvoiceDirect(sch, ms, idx)}
                                              disabled={actionLoading[dlKey]}
                                              title="Download phase invoice PDF"
                                              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 disabled:opacity-60 whitespace-nowrap"
                                            >
                                              {actionLoading[dlKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                              Download
                                            </button>
                                          )}

                                          {['invoice_generated', 'approved', 'partially_paid'].includes(ms.status) && (ms.receiptGenerated || ms.receiptNo) && (
                                            <button
                                              onClick={() => downloadMilestoneReceipt(sch, ms, idx)}
                                              title="Download customer payment receipt PDF"
                                              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap shadow-sm"
                                            >
                                              <Download className="w-3 h-3" />
                                              Download Receipt
                                            </button>
                                          )}
                                        </>
                                      )}

                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {allocMeta && (
        <AllocateAdvanceModal
          scheduleId={allocMeta.sch._id}
          milestone={allocMeta.ms}
          onClose={() => setAllocMeta(null)}
          onAllocated={() => {
            setAllocMeta(null);
            showToast('Customer advance allocated to milestone successfully!');
            fetchSchedules();
          }}
        />
      )}

      {historyMeta && (
        <PaymentHistoryModal
          schedule={historyMeta.sch}
          milestone={schedules.find(x => x._id === historyMeta.sch._id)?.milestones.find(m => m._id === historyMeta.ms._id) || historyMeta.ms}
          onClose={() => setHistoryMeta(null)}
        />
      )}

      {payMeta && (
        <RecordPaymentModal
          schedule={payMeta.sch}
          milestone={payMeta.ms}
          onClose={() => setPayMeta(null)}
          onRecorded={() => {
            setPayMeta(null);
            showToast('Customer payment recorded successfully!');
            fetchSchedules();
          }}
        />
      )}

      {/* Reject / Request Revision Modal */}
      {rejectModal.open && (
        <Modal
          open={rejectModal.open}
          onClose={() => setRejectModal({ open: false, sch: null, ms: null, reason: '', loading: false })}
          title="Reject / Request Revision for Tax Invoice"
          size="md"
        >
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
              <span className="font-semibold">Milestone:</span> {rejectModal.ms?.name} ({rejectModal.sch?.quotationNumber || rejectModal.sch?.clientName || 'Schedule'})
              {rejectModal.ms?.invoiceNumber && <span className="block mt-0.5 font-mono">Invoice #: {rejectModal.ms?.invoiceNumber}</span>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Reason & Notes for Revision <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={4}
                value={rejectModal.reason}
                onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Specify what needs to be corrected or revised (e.g. incorrect line items, revised scope, updated billing details)..."
                className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRejectModal({ open: false, sch: null, ms: null, reason: '', loading: false })}
                disabled={rejectModal.loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={submitRejectInvoice}
                disabled={rejectModal.loading || !rejectModal.reason.trim()}
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              >
                {rejectModal.loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                Confirm Rejection / Request Revision
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Milestone Reminder Modal ─────────────────────────────────────── */}
      {reminderModal && (
        <Modal open={reminderModal.open} onClose={() => setReminderModal(null)} title="Create Milestone Reminder" size="md">
          <div className="space-y-4">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-lg text-xs text-indigo-800 dark:text-indigo-300">
              <span className="font-semibold">Milestone:</span> {reminderModal.ms.name} ({reminderModal.ms.percentage}%) — {reminderModal.sch.clientName}
              {reminderModal.sch.quotationNumber && <span className="block mt-0.5 font-mono">{reminderModal.sch.quotationNumber}</span>}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Reminder Title</label>
              <Input
                value={reminderTitle}
                onChange={(e) => setReminderTitle(e.target.value)}
                placeholder="e.g. Follow up on payment"
                className="text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">When</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'tomorrow_morning', label: 'Tomorrow 9:00 AM' },
                  { id: 'in_3_days', label: 'In 3 Days' },
                  { id: 'on_due_date', label: 'On Due Date' },
                  { id: 'custom', label: 'Custom Date & Time' },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setReminderPreset(p.id as any)}
                    className={`p-2 rounded-xl border text-xs font-medium text-left transition-all ${reminderPreset === p.id
                        ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 font-bold'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
                      }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {reminderPreset === 'custom' && (
                <Input
                  type="datetime-local"
                  value={reminderCustomDate}
                  onChange={(e) => setReminderCustomDate(e.target.value)}
                  className="text-xs mt-2"
                />
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Notes (Optional)</label>
              <Textarea
                value={reminderNotes}
                onChange={(e) => setReminderNotes(e.target.value)}
                placeholder="Additional details for this reminder..."
                className="text-xs min-h-[70px] resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
              <Button variant="outline" size="sm" onClick={() => setReminderModal(null)} disabled={creatingReminder}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveMilestoneReminder} disabled={creatingReminder || !reminderTitle.trim()} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-1.5">
                {creatingReminder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Create Reminder
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default MilestoneDashboardPage;
